import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ArrowLeft, ArrowRight, ChevronRight, Heart, ThumbsUp, Users, Smartphone,
  CheckCircle, MessageSquare, Sparkles, MousePointer, Hand, Type, FileText, 
  Square, ArrowUpRight, Smile, Eraser, Trash2, Check, Play, Pause, RotateCcw, 
  Music, Volume2, VolumeX, Plus, Minus, HelpCircle
} from 'lucide-react';
import { 
  ToolType, ShapeType, StickyColor, StampType, StickyNote, ShapeElement, 
  DrawPath, StampInstance, Connection, PeerCursor 
} from './types';

import PrivacyPolicy from './PrivacyPolicy';
import TermsAndConditions from './TermsAndConditions';
import LegalNotice from './LegalNotice';



interface HeaderProps {
  onOpenWhiteboard: () => void;
  onGetStarted: () => void;
  onGoHome?: () => void;
}

function Header({ onOpenWhiteboard, onGetStarted, onGoHome }: HeaderProps) {
  return (
    <header className="sticky top-0 z-50 flex items-center justify-between border-b border-zinc-100/80 bg-white/70 backdrop-blur-md px-6 py-3.5 transition-all md:px-12">
      {/* Left: Brand logo */}
      <div className="flex items-center gap-2.5">
        <a 
          href="/" 
          onClick={(e) => {
            if (onGoHome) {
              e.preventDefault();
              onGoHome();
            }
          }}
          className="flex items-center gap-2.5 outline-none"
        >
          <div className="relative w-6 h-6 flex items-center justify-center mr-1">
            <div className="absolute top-[3px] left-[5px] w-[13px] h-[16px] rounded-[2px] bg-[#ec4899] rotate-[10deg] opacity-85 shadow-xs" />
            <div className="absolute top-[2px] left-[3px] w-[13px] h-[16px] rounded-[2px] bg-[#8b5cf6] rotate-[-5deg] opacity-90 shadow-xs" />
            <div className="absolute top-[1px] left-[1px] w-[13px] h-[16px] rounded-[2px] bg-[#f59e0b] shadow-xs" />
          </div>
          <span className="font-sans text-[20px] font-extrabold tracking-tight text-zinc-900 flex items-center">
            Design Studio
            <sup className="text-[10px] text-purple-600 bg-purple-50 px-1 py-0.2 rounded ml-1 font-extrabold align-super">BETA</sup>
          </span>
        </a>
      </div>

      {/* Middle Links: standard navigational desktop list */}
      <nav className="hidden items-center gap-8 text-[14px] font-semibold text-zinc-655 lg:flex">
        <a href="#features" className="hover:text-zinc-950 transition-colors">Product</a>
        <a href="#templates" className="hover:text-zinc-950 transition-colors">Solutions</a>
        <a href="#integrations" className="hover:text-zinc-950 transition-colors">Marketplace</a>
        <a onClick={onGetStarted} href="#whiteboard" className="hover:text-zinc-950 transition-colors">
          Resources
        </a>
      </nav>

      {/* Right actions */}
      <div className="flex items-center gap-3 md:gap-5">
        <a href="#login" className="text-[14px] font-semibold text-zinc-655 hover:text-zinc-950 transition-colors">
          Sign In
        </a>
        
        <button 
          onClick={onGetStarted}
          className="rounded-full bg-zinc-950 px-5 py-2 text-[14.5px] font-semibold text-white shadow-sm hover:bg-black active:scale-[0.98] transition-all cursor-pointer"
        >
          Sign Up
        </button>
      </div>
    </header>
  );
}

const FOOTER_COLUMNS = [
  {
    title: 'Workspace',
    links: [
      { text: 'Art studio', href: 'https://artstudio.caselgrid.tech' },
      { text: 'Crm', href: 'https://crm.caselgrid.tech' },
      { text: 'Crm plus', href: 'https://sentral.caselgrid.tech' },
      { text: 'Projects', href: 'https://projects.caselgrid.tech' },
      { text: 'Screen recorder', href: 'https://screenrecorder.caselgrid.tech' },
      { text: 'Billing', href: 'https://billing.caselgrid.tech' },
      { text: 'Workspace Suite', href: 'https://workspace.caselgrid.tech' },
      { text: 'Sentral', href: 'https://sentral.caselgrid.tech' },
      { text: 'Calender', href: 'https://calender.caselgrid.tech' },
      { text: 'Docs', href: 'https://docs.caselgrid.tech' }
    ]
  },
  {
    title: 'Use Cases',
    links: [
      { text: 'UI design', href: '#' },
      { text: 'UX design', href: '#' },
      { text: 'Wireframing', href: '#' },
      { text: 'Diagramming', href: '#' },
      { text: 'Prototyping', href: '#' },
      { text: 'Brainstorming', href: '#' },
      { text: 'Presentation Maker', href: '#' },
      { text: 'Online whiteboard', href: '#' },
      { text: 'Strategic planning', href: '#' },
      { text: 'Mind mapping', href: '#' },
      { text: 'Concept map', href: '#' },
      { text: 'AI app builder', href: '#' },
      { text: 'AI prototype generator', href: '#' }
    ]
  },
  {
    title: 'Resources',
    links: [
      { text: 'Blog', href: '#' },
      { text: 'Best practices', href: '#' },
      { text: 'Gradient Library', href: '#' },
      { text: 'Gradient Generator', href: '#' },
      { text: 'GIF maker', href: '#' },
      { text: 'Color wheel', href: '#' },
      { text: 'Colors palette', href: '#' },
      { text: 'Color picker', href: '#' },
      { text: 'Font library', href: '#' },
      { text: 'Templates library', href: '#' },
      { text: 'Developers hub', href: '#' },
      { text: 'Integrations list', href: '#' },
      { text: 'Affiliate program', href: '#' }
    ]
  },
  {
    title: 'Company',
    links: [
      { text: 'Events', href: '#' },
      { text: 'Customers', href: '#' },
      { text: 'Careers', href: '#' },
      { text: 'Newsroom', href: '#' },
      { text: 'Investors', href: '#' },
      { text: 'Figma Ventures', href: '#' }
    ]
  }
];

interface FooterProps {
  onNavigate: (view: 'home' | 'privacy' | 'terms' | 'legal') => void;
}

function Footer({ onNavigate }: FooterProps) {
  return (
    <footer className="border-t border-zinc-100 bg-white px-6 py-16 md:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          
          {/* Logo and Social links */}
          <div className="col-span-2 lg:col-span-1 flex flex-col justify-between gap-6">
            <div className="space-y-4">
              <a 
                href="/" 
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate('home');
                }}
                className="flex items-center gap-2.5 outline-none"
              >
                <div className="relative w-6 h-6 flex items-center justify-center mr-1">
                  <div className="absolute top-[3px] left-[5px] w-[13px] h-[16px] rounded-[2px] bg-[#4285f4] rotate-[10deg] opacity-80 shadow-xs" />
                  <div className="absolute top-[2px] left-[3px] w-[13px] h-[16px] rounded-[2px] bg-[#ea4335] rotate-[-5deg] opacity-90 shadow-xs" />
                  <div className="absolute top-[1px] left-[1px] w-[13px] h-[16px] rounded-[2px] bg-[#fbbc05] shadow-xs" />
                </div>
                <span className="font-sans text-xl font-bold tracking-tight text-zinc-900 flex items-center">
                  Design Studio
                  <sup className="text-[9px] text-purple-600 bg-purple-50 px-1 py-0.2 rounded ml-1 font-extrabold align-super">BETA</sup>
                </span>
              </a>
              <p className="text-xs text-zinc-500 leading-relaxed max-w-[200px]">
                Connect teams, draft solutions, and ship high impact products with real-time vector alignment.
              </p>
            </div>

            {/* Social SVGs */}
            <div className="flex items-center gap-3">
              <a href="#" aria-label="Design Studio on X" className="rounded-full bg-zinc-50 p-2 text-zinc-650 hover:bg-zinc-100 hover:text-black transition-colors">
                <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="#" aria-label="Design Studio on YouTube" className="rounded-full bg-zinc-50 p-2 text-zinc-655 hover:bg-zinc-100 hover:text-black transition-colors">
                <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.163a3.003 3.003 0 0 0-2.11-2.11C19.52 3.518 12 3.518 12 3.518s-7.52 0-9.388.535a3.003 3.003 0 0 0-2.11 2.11C0 8.03 0 12 0 12s0 3.97.512 5.837a3.003 3.003 0 0 0 2.11 2.11c1.868.535 9.388.535 9.388.535s7.52 0 9.388-.535a3.003 3.003 0 0 0 2.11-2.11c.512-1.867.512-5.837.512-5.837s0-3.97-.512-5.837zm-14.28 9.53V8.307L15.4 12l-6.182 3.693z" />
                </svg>
              </a>
              <a href="#" aria-label="Design Studio on Instagram" className="rounded-full bg-zinc-50 p-2 text-zinc-655 hover:bg-zinc-100 hover:text-black transition-colors">
                <svg className="h-[14px] w-[14px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
                  <path d="M16 11.37a4 4 0 1 1-7.88-2.37c.39-1.29 1.48-2.25 2.88-2.5 1.4-.25 2.8.25 3.5 1.38.7 1.13.7 2.62.11 3.75z"/>
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Map through columns */}
          {FOOTER_COLUMNS.map((col, i) => (
            <div key={i} className="space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-800">
                {col.title}
              </h4>
              <ul className="space-y-2 text-xs font-semibold text-zinc-500">
                {col.links.map((link, j) => (
                  <li key={j} className="flex items-center gap-1.5">
                    <a 
                      href={link.href} 
                      target={col.title === 'Workspace' || (link.href && link.href.includes('caselgrid.tech')) ? '_blank' : undefined}
                      rel={col.title === 'Workspace' || (link.href && link.href.includes('caselgrid.tech')) ? 'noopener noreferrer' : undefined}
                      className="hover:text-black hover:underline transition-all"
                    >
                      {link.text}
                    </a>
                    {link.label && (
                      <span className="rounded bg-purple-50 px-1 py-0.5 text-[8px] font-bold text-purple-700 uppercase">
                        {link.label}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

        </div>

        {/* Footer bottom standard compliance bar */}
        <div className="mt-16 flex flex-col md:flex-row md:items-center justify-between gap-4 border-t border-zinc-100 pt-8 text-[11px] font-bold text-zinc-400">
          <div>
            © 2026 Design Studio • Developed and Maintained by Caselgrid
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <button onClick={() => onNavigate('privacy')} className="hover:text-zinc-650 cursor-pointer font-bold transition-colors">Privacy Policy</button>
            <button onClick={() => onNavigate('terms')} className="hover:text-zinc-650 cursor-pointer font-bold transition-colors">Terms of Service</button>
            <button onClick={() => onNavigate('legal')} className="hover:text-zinc-650 cursor-pointer font-bold transition-colors">Legal Notice</button>
            <span className="text-zinc-200">|</span>
            <span className="text-zinc-500 uppercase tracking-[0.1em] text-[9px] font-extrabold bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded select-none">BETA Platform</span>
          </div>
        </div>

        {/* Workspace Suite Links */}
        <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 items-center text-[11px] font-bold text-zinc-400 border-t border-zinc-100/50 pt-4">
          <span className="text-zinc-500">Workspace Suite:</span>
          <a href="https://workspace.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Workspace Suite</a>
          <span className="text-zinc-200">|</span>
          <a href="https://sentral.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Sentral</a>
          <span className="text-zinc-200">|</span>
          <a href="https://calender.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Calender</a>
          <span className="text-zinc-200">|</span>
          <a href="https://docs.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Docs</a>
          <span className="text-zinc-200">|</span>
          <a href="https://artstudio.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Art studio</a>
          <span className="text-zinc-200">|</span>
          <a href="https://crm.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Crm</a>
          <span className="text-zinc-200">|</span>
          <a href="https://sentral.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Crm plus</a>
          <span className="text-zinc-200">|</span>
          <a href="https://projects.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Projects</a>
          <span className="text-zinc-200">|</span>
          <a href="https://screenrecorder.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Screen recorder</a>
          <span className="text-zinc-200">|</span>
          <a href="https://billing.caselgrid.tech" target="_blank" rel="noopener noreferrer" className="hover:text-black hover:underline transition-all">Billing</a>
        </div>
      </div>
    </footer>
  );
}

interface FeatureSectionsProps {
  onSelectTemplate: (templateType: string) => void;
}

// 1. Carousel Data for "The right tool for any job"
const JOB_SLIDES = [
  {
    id: 'brainstorming',
    title: 'Brainstorming',
    description: 'Riff and build on ideas in real time to discover new ways to tackle tough problems.',
    tag: 'Riff together in space',
    stickies: [
      { text: 'Add related posts to each article detail page', color: 'bg-yellow-100 border-yellow-200 text-yellow-900', label: 'Lauren', x: '10%', y: '15%', rot: '-3deg' },
      { text: 'CATEGORY FILTERS!', color: 'bg-emerald-100 border-emerald-200 text-emerald-900', label: 'Jefferson', x: '50%', y: '25%', rot: '4deg' },
      { text: 'Dynamic infinite list suggestions logic', color: 'bg-orange-100 border-orange-200 text-orange-900', label: 'Sarah', x: '25%', y: '55%', rot: '-1deg' },
    ]
  },
  {
    id: 'diagramming',
    title: 'Diagramming',
    description: 'Work through complex user flows, processes, and systems with shapes and connectors that snap to grid.',
    tag: 'Map systems with clarity',
    stickies: [
      { text: 'Model Flow: Launch', color: 'bg-purple-100 border-purple-200 text-purple-900', label: 'System', x: '12%', y: '30%', rot: '0deg', shape: 'circle' },
      { text: 'Create account', color: 'bg-white border-zinc-300 text-zinc-800', label: 'Fiona', x: '45%', y: '30%', rot: '0deg', shape: 'rect' },
      { text: 'Verify profile', color: 'bg-amber-100 border-amber-200 text-amber-900', label: 'Deepak', x: '78%', y: '30%', rot: '0deg', shape: 'diamond' },
    ]
  },
  {
    id: 'meetings',
    title: 'Meetings',
    description: 'Run engaging standups, retrospectives, and icebreakers that keep everyone on the same track.',
    tag: 'Engage team alignment',
    stickies: [
      { text: 'Standup: What did you build today?', color: 'bg-blue-100 border-blue-200 text-blue-900', label: 'Host', x: '20%', y: '20%', rot: '1deg' },
      { text: 'Finished custom font parameters styling', color: 'bg-green-100 border-green-200 text-green-900', label: 'Jessica', x: '55%', y: '45%', rot: '-2deg' },
    ]
  },
  {
    id: 'agile',
    title: 'Agile workflows',
    description: 'Keep your team aligned with visual sprint planning, daily standups, and retrospective boards.',
    tag: 'Agile alignment',
    stickies: [
      { text: 'Sprint 24 Goal: Complete checkout page', color: 'bg-[#FFCCD9] border-[#FFA8BF] text-[#7E1F3C]', label: 'PM', x: '15%', y: '25%', rot: '2deg' },
      { text: 'In Progress: Integration testing', color: 'bg-[#B3E4FF] border-[#84CEFA] text-[#004770]', label: 'Sarah', x: '50%', y: '55%', rot: '-3deg' },
    ]
  },
  {
    id: 'planning',
    title: 'Strategy & planning',
    description: 'Chart a path forward with SWOT analysis, customer journey maps, and matrix templates.',
    tag: 'Chart the roadmap',
    stickies: [
      { text: 'SWOT Analysis: Core Competencies', color: 'bg-[#FFF1AC] border-[#EBD268] text-[#5F4D00]', label: 'Team', x: '25%', y: '15%', rot: '-1deg' },
      { text: 'Milestone 1: Prototype completion', color: 'bg-[#C2F5D7] border-[#92EAA8] text-[#0A552C]', label: 'Lead', x: '60%', y: '40%', rot: '3deg' },
    ]
  }
];

// 2. Carousel Data for "Templates"
const TEMPLATE_CARDS = [
  {
    id: 'kickoff',
    title: 'Project kickoff',
    description: 'Establish goals, alignments, team roles, and initial milestones in a visual layout.',
    category: 'planning',
    tag: 'Kickstart conversation',
    color: 'bg-emerald-50'
  },
  {
    id: 'flowchart',
    title: 'Flow chart',
    description: 'Map workflows, application decision logic trees, routes, and organizational sequences.',
    category: 'diagramming',
    tag: 'Visualize connections',
    color: 'bg-amber-50'
  },
  {
    id: 'mindmap',
    title: 'Mind map',
    description: 'Deconstruct complex topics visually, branching sub-topics from a single central core.',
    category: 'brainstorming',
    tag: 'Deconstruct ideas',
    color: 'bg-purple-50'
  },
  {
    id: 'retro',
    title: 'Retrospective',
    description: 'Reflect on what went well and what can be improved in your team\'s sprints.',
    category: 'agile',
    tag: 'Team retrospectives',
    color: 'bg-pink-50'
  },
  {
    id: 'journey',
    title: 'Customer journey',
    description: 'Trace and understand user steps, pain points, and decisions across touchpoints.',
    category: 'research',
    tag: 'Map user journey',
    color: 'bg-indigo-50'
  },
  {
    id: 'swot',
    title: 'SWOT matrix',
    description: 'Explore strengths, weaknesses, opportunities, and threats for a master strategy.',
    category: 'planning',
    tag: 'Analyze strategy',
    color: 'bg-blue-50'
  },
  {
    id: 'empathy',
    title: 'Empathy map',
    description: 'Capture what users say, do, think, and feel to build deeper product designs.',
    category: 'research',
    tag: 'Understand feelings',
    color: 'bg-rose-50'
  },
  {
    id: 'standup',
    title: 'Daily standup',
    description: 'Sync status updates, align on next build steps, check block issues, and log times.',
    category: 'meetings',
    tag: 'Coordinate daily standup',
    color: 'bg-yellow-50'
  }
];

function FeatureSections({ onSelectTemplate }: FeatureSectionsProps) {

  // "Right tool for any job" slidable state
  const [jobIndex, setJobIndex] = useState(0);

  // Template slider state
  const [templateStartIndex, setTemplateStartIndex] = useState(0);

  // Feature 2: Stamp feedback counts simulation
  const [feedbackVote, setFeedbackVote] = useState({ optA: 18, optB: 12 });
  const [activeBounces, setActiveBounces] = useState<Array<{ id: number; emoji: string }>>([]);

  // Feature 4: Spotlight state
  const [spotlightActive, setSpotlightActive] = useState(false);
  const [spotlightCoords, setSpotlightCoords] = useState({ x: 100, y: 100 });

  // Handle template sliding
  const handlePrevTemplate = () => {
    setTemplateStartIndex(prev => Math.max(0, prev - 1));
  };
  const handleNextTemplate = () => {
    setTemplateStartIndex(prev => Math.min(TEMPLATE_CARDS.length - 3, prev + 1));
  };

  const triggerReactionParticles = (e: React.MouseEvent, type: 'heart' | 'thumb', target: 'optA' | 'optB') => {
    setFeedbackVote(prev => ({
      ...prev,
      [target]: prev[target] + 1
    }));

    const newBounce = {
      id: Date.now() + Math.random(),
      emoji: type === 'heart' ? '❤️' : '👍'
    };

    setActiveBounces(curr => [...curr, newBounce]);
    setTimeout(() => {
      setActiveBounces(curr => curr.filter(b => b.id !== newBounce.id));
    }, 1500);
  };

  const handleSpotlightMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!spotlightActive) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setSpotlightCoords({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  return (
    <div className="flex flex-col gap-24 py-12">
      
      {/* SECTION 1: "The right tool for any job" */}
      <section className="flex flex-col gap-8 text-zinc-900" id="features">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="max-w-xl">
            <h2 className="font-sans text-3xl font-extrabold md:text-4xl text-zinc-900 tracking-tight leading-tight">
              The right tool for any job
            </h2>
            <p className="mt-3 text-[16px] text-zinc-600 leading-relaxed">
              From brainstorm to build and everything in between, FigJam makes it easy for teams to collaborate in real time.
            </p>
          </div>

          {/* Slider indicators and navigation */}
          <div className="flex items-center gap-4">
            <span className="font-mono text-[13px] font-semibold text-zinc-500">
              {jobIndex + 1} of 3
            </span>
            <div className="flex items-center gap-1.5">
              <button 
                onClick={() => setJobIndex(prev => (prev === 0 ? 2 : prev - 1))}
                className="rounded-full border border-zinc-200 bg-white p-2.5 text-zinc-700 hover:bg-zinc-50 hover:text-black transition-all shadow-sm active:scale-95"
                aria-label="Previous slide"
              >
                <ArrowLeft size={16} />
              </button>
              <button 
                onClick={() => setJobIndex(prev => (prev === 2 ? 0 : prev + 1))}
                className="rounded-full border border-zinc-200 bg-white p-2.5 text-zinc-700 hover:bg-zinc-50 hover:text-black transition-all shadow-sm active:scale-95"
                aria-label="Next slide"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Triple horizontally slidable or side-by-side cards layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {JOB_SLIDES.slice(jobIndex, jobIndex + 3).map((item) => (
            <div 
              key={item.id} 
              className="flex flex-col justify-between rounded-3xl border border-zinc-200 bg-white p-6 shadow-sm hover:shadow-md transition-all h-[520px]"
            >
              <div>
                {/* Visual mockup box on top */}
                <div className="bg-zinc-50 rounded-2xl h-[260px] border border-zinc-100 flex items-center justify-center relative overflow-hidden dot-grid p-4 mb-5">
                  <div className="absolute top-3 left-3 bg-white/80 backdrop-blur-sm rounded-full px-2.5 py-0.5 text-[9px] font-bold text-zinc-500 border border-zinc-100 shadow-sm uppercase">
                    {item.tag}
                  </div>
                  
                  {item.id === 'brainstorming' && (
                    <div className="w-full h-full relative flex flex-col justify-center gap-3">
                      <div className="absolute top-10 left-4 bg-[#FFEB3B]/90 border border-[#fdd835] rounded-lg p-3 shadow-md max-w-[130px] -rotate-3">
                        <p className="font-sans text-[10px] font-bold text-yellow-950">Search?</p>
                        <span className="block text-[8px] font-bold opacity-60 text-right mt-1.5">— Lauren</span>
                      </div>
                      <div className="absolute top-5 right-4 bg-[#4CAF50]/90 border border-[#43a047] rounded-lg p-3 shadow-md max-w-[140px] rotate-3 text-white">
                        <p className="font-sans text-[10px] font-bold">CATEGORY FILTERS!</p>
                        <span className="block text-[8px] font-bold opacity-80 text-right mt-1.5">— Jefferson</span>
                      </div>
                      <div className="absolute bottom-4 left-10 bg-[#FF9800]/90 border border-[#fb8c00] rounded-lg p-3 shadow-md w-[80%] rotate-1">
                        <p className="font-sans text-[10px] font-bold text-orange-950">Dynamic suggestions list</p>
                        <span className="block text-[8px] font-bold opacity-60 text-right mt-1.5">— Sarah</span>
                      </div>
                    </div>
                  )}

                  {item.id === 'diagramming' && (
                    <div className="w-full h-full relative flex flex-col justify-center items-center gap-2">
                      <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="30%" y1="50%" x2="55%" y2="50%" stroke="#111827" strokeWidth="2" strokeDasharray="3" />
                        <line x1="55%" y1="50%" x2="80%" y2="50%" stroke="#111827" strokeWidth="2" />
                      </svg>
                      
                      <div className="absolute left-2 top-[38%] bg-[#E57373]/90 border border-[#e53935] rounded-full h-11 w-11 flex items-center justify-center shadow-md -rotate-6">
                        <span className="text-[8px] font-bold text-white text-center">Model</span>
                      </div>
                      <div className="absolute bg-white border-2 border-purple-500 rounded-lg p-3 shadow-md max-w-[100px] z-10">
                        <p className="font-sans text-[9px] font-extrabold text-purple-950 leading-none">Step 1</p>
                        <span className="block text-[7px] text-zinc-500 mt-1">Verify logic</span>
                      </div>
                      <div className="absolute right-2 top-[38%] bg-[#81C784]/90 border border-[#4caf50] rounded-lg p-3 shadow-md max-w-[100px] rotate-6 text-white">
                        <span className="text-[8px] font-bold">Step 2</span>
                      </div>
                    </div>
                  )}

                  {item.id === 'meetings' && (
                    <div className="w-full h-full relative flex flex-col justify-center items-center gap-3">
                      <span className="bg-[#4FC3F7]/90 border border-[#03a9f4] rounded-lg p-3 shadow-md max-w-[160px] text-zinc-900 block font-sans text-[10px] font-bold">
                        Standup: What is complete?
                      </span>
                      <span className="bg-[#81C784]/90 border border-[#4caf50] rounded-lg p-3 shadow-md max-w-[170px] text-zinc-900 block font-sans text-[10px] font-semibold -rotate-2">
                        Drafted dynamic whiteboard logic
                      </span>
                    </div>
                  )}

                  {item.id === 'agile' && (
                    <div className="w-full h-full relative flex flex-col justify-center items-center gap-2">
                      <div className="bg-[#FF8A80]/90 border border-[#ff5252] rounded-lg p-3 shadow-md text-center max-w-[140px]">
                        <span className="text-[10px] font-bold text-[#b71c1c]">Sprint Plan Alpha</span>
                      </div>
                    </div>
                  )}

                  {item.id === 'planning' && (
                    <div className="w-full h-full relative flex flex-col justify-center items-center">
                      <div className="bg-[#FFE082]/90 border border-[#ffca28] rounded-lg p-3 shadow text-center max-w-[140px] rotate-2">
                        <span className="text-[10px] font-bold text-[#e65100]">Milestones Matrix</span>
                      </div>
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-extrabold font-sans text-zinc-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-[13px] text-zinc-500 leading-relaxed font-medium">
                  {item.description}
                </p>
              </div>

              <button 
                onClick={() => {
                  onSelectTemplate(item.id);
                  const arena = document.getElementById('whiteboard-arena');
                  if (arena) arena.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full mt-4 text-center rounded-xl bg-zinc-900 text-white hover:bg-black px-4 py-2.5 text-xs font-bold transition-all shadow active:scale-[0.98]"
              >
                Learn more
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 2: "Get a running start with templates" */}
      <section className="rounded-3xl bg-[#FFDE6A]/20 p-6 md:p-12 border border-[#FFDE6A]/45" id="templates">
        
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <h2 className="font-sans text-3xl font-extrabold text-zinc-900 tracking-tight">
              Get a running start with templates
            </h2>
            <p className="mt-2.5 text-[15px] text-zinc-650 max-w-xl font-medium">
              Kickstart conversations and projects with over <span className="underline decoration-yellow-600 underline-offset-4 decoration-2">300 ready-made templates</span>—or make your own.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <span className="font-mono text-[13px] font-bold text-zinc-600">
              1 of 8
            </span>
            <div className="flex items-center gap-1.5">
              <button 
                onClick={handlePrevTemplate}
                disabled={templateStartIndex === 0}
                className={`rounded-full border border-zinc-200 bg-white p-2.5 text-zinc-750 transition-all shadow-sm active:scale-95 ${templateStartIndex === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-zinc-50'}`}
                aria-label="Previous templates"
              >
                <ArrowLeft size={16} />
              </button>
              <button 
                onClick={handleNextTemplate}
                disabled={templateStartIndex >= TEMPLATE_CARDS.length - 3}
                className={`rounded-full border border-zinc-200 bg-white p-2.5 text-zinc-750 transition-all shadow-sm active:scale-95 ${templateStartIndex >= TEMPLATE_CARDS.length - 3 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-zinc-50'}`}
                aria-label="Next templates"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Grid row of side-by-side template preview cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TEMPLATE_CARDS.slice(templateStartIndex, templateStartIndex + 3).map((item) => (
            <div 
              key={item.id} 
              className="flex flex-col justify-between rounded-2xl border border-zinc-200/60 bg-white p-5 shadow-sm hover:shadow-md transition-all h-[420px]"
            >
              <div>
                <div className={`h-36 ${item.color} rounded-xl border border-zinc-100 flex items-center justify-center relative overflow-hidden mb-4 p-4`}>
                  <div className="absolute inset-0 opacity-15 dot-grid"></div>
                  
                  {item.id === 'kickoff' && (
                    <div className="flex gap-2">
                      <span className="bg-emerald-200 text-emerald-800 text-[8px] font-bold px-2 py-1 rounded">Project Goals</span>
                      <span className="bg-blue-200 text-blue-800 text-[8px] font-bold px-2 py-1 rounded">Roles</span>
                    </div>
                  )}

                  {item.id === 'flowchart' && (
                    <div className="flex items-center gap-2">
                      <span className="bg-yellow-200 text-yellow-800 text-[8px] font-bold px-2 py-1 rounded">Start</span>
                      <span className="text-zinc-400 text-xs">→</span>
                      <span className="bg-purple-200 text-purple-850 text-[8px] font-bold px-2 py-1 rounded">Step</span>
                    </div>
                  )}

                  {item.id === 'mindmap' && (
                    <div className="h-10 w-24 bg-indigo-100 border-2 border-indigo-500 rounded-full flex items-center justify-center text-[8px] text-indigo-950 font-bold">
                      Main Hub
                    </div>
                  )}

                  {item.id === 'retro' && (
                    <div className="flex gap-1.5">
                      <div className="bg-emerald-100 p-1 w-8 h-10 rounded border border-emerald-300"></div>
                      <div className="bg-red-100 p-1 w-8 h-10 rounded border border-red-300"></div>
                    </div>
                  )}

                  {item.id === 'journey' && (
                    <div className="flex gap-1 items-center">
                      <span className="text-[8px] font-bold text-zinc-500">Discover</span>
                      <span>→</span>
                      <span className="text-[8px] font-bold text-zinc-500">Adopt</span>
                    </div>
                  )}

                  {item.id === 'swot' && (
                    <span className="font-bold text-xs text-blue-800">S.W.O.T Matrix</span>
                  )}

                  {item.id === 'empathy' && (
                    <span className="font-bold text-xs text-rose-800">Empathy Mapping</span>
                  )}

                  {item.id === 'standup' && (
                    <span className="font-bold text-[10px] text-yellow-900 border border-yellow-350 p-2 bg-yellow-50 rounded">Action Items</span>
                  )}
                </div>

                <h3 className="text-lg font-bold font-sans text-zinc-900 mb-1">
                  {item.title}
                </h3>
                <p className="text-[12.5px] text-zinc-500 leading-normal font-medium mb-4">
                  {item.description}
                </p>
              </div>

              <button 
                onClick={() => {
                  onSelectTemplate(item.id);
                  const arena = document.getElementById('whiteboard-arena');
                  if (arena) arena.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full text-center rounded-xl bg-zinc-950 text-white hover:bg-black px-4 py-2.5 text-xs font-bold transition-all shadow"
              >
                Start with this template
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 3 (NEW/RESTORED): "A visual whiteboard for your coding agent" */}
      <section className="bg-white border-y border-zinc-100 p-12 md:p-14 text-center max-w-4xl mx-auto space-y-5">
        <h2 className="font-sans text-3xl font-extrabold md:text-4xl text-zinc-900 tracking-tight">
          A visual whiteboard for your coding agent
        </h2>
        <p className="text-[15px] text-zinc-600 leading-relaxed max-w-2xl mx-auto font-medium">
          FigJam lets you generate diagrams that map out what your AI agent is building based on your brainstorms, project plans, and codebases. This way, your whole team stays aligned on what's being built.
        </p>
        <button 
          onClick={() => {
            const arena = document.getElementById('whiteboard-arena');
            if (arena) arena.scrollIntoView({ behavior: 'smooth' });
          }}
          className="rounded-full bg-zinc-950 text-white hover:bg-black font-bold px-8 py-3 text-xs transition-colors shadow-md active:scale-95"
        >
          Learn more
        </button>
      </section>

      {/* SECTION 4: "Features that keep everyone on the same page" */}
      <section className="flex flex-col gap-12">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <h2 className="font-sans text-3xl font-extrabold text-zinc-900 tracking-tight text-[28px] sm:text-3xl">
            Features that keep everyone on the same page
          </h2>
          <p className="text-[14px] text-zinc-500 leading-relaxed font-semibold">
            Empower everyone throughout the product development process to contribute, give feedback, and make better decisions, faster.
          </p>
        </div>

        {/* Feature grid quadrant section matches layout precisely */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* CARD 1: Keep conversations going */}
          <div className="rounded-2xl border border-zinc-200/70 p-6 bg-white shadow-sm flex flex-col justify-between">
            <div className="space-y-2 mb-6">
              <h3 className="text-lg font-bold font-sans text-zinc-900">
                Keep conversations going
              </h3>
              <p className="text-[13px] text-zinc-600 leading-relaxed">
                Talk things out with audio and live chat, or use comments to give feedback on the go.
              </p>
              <button 
                onClick={() => {
                  const arena = document.getElementById('whiteboard-arena');
                  if (arena) arena.scrollIntoView({ behavior: 'smooth' });
                }}
                className="mt-2 block bg-zinc-950 text-white hover:bg-black px-4.5 py-2 rounded-lg text-xs font-bold shadow active:scale-95 transition-all w-fit"
              >
                Try for free
              </button>
            </div>

            {/* Teammate voices audio chat drifting cursors visual sandbox */}
            <div className="bg-[#FAF9FB] rounded-xl border border-zinc-100 p-4 h-48 relative overflow-hidden">
              <div className="flex gap-2 mb-3">
                {/* 4 user avatars speaking */}
                <div className="relative">
                  <div className="h-8 w-8 rounded-full bg-purple-500 border border-white font-bold text-[9px] text-white flex items-center justify-center">JA</div>
                  <div className="absolute -bottom-1 -right-1 h-3.5 w-3.5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <div className="h-1 w-1 bg-white rounded-full animate-ping"></div>
                  </div>
                </div>
                <div className="relative">
                  <div className="h-8 w-8 rounded-full bg-yellow-400 border border-white font-bold text-[9px] text-zinc-900 flex items-center justify-center">CA</div>
                  <div className="absolute -bottom-1 -right-1 h-3.5 w-3.5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <div className="h-1 w-1 bg-white rounded-full animate-ping"></div>
                  </div>
                </div>
                <div className="relative">
                  <div className="h-8 w-8 rounded-full bg-blue-500 border border-white font-bold text-[9px] text-white flex items-center justify-center">IN</div>
                  <div className="absolute -bottom-1 -right-1 h-3.5 w-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <div className="relative">
                  <div className="h-8 w-8 rounded-full bg-zinc-400 border border-white font-bold text-[9px] text-white flex items-center justify-center">ST</div>
                </div>
              </div>

              {/* Animated cursors roaming */}
              <div className="absolute inset-0 pointer-events-none">
                <span className="absolute top-[48%] left-[10%] flex items-center gap-1.5 bg-purple-600 rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow">
                  <span>Jason</span>
                </span>
                <span className="absolute top-[35%] left-[45%] flex items-center gap-1.5 bg-yellow-500 rounded-full px-2.5 py-0.5 text-[9px] font-bold text-zinc-900 shadow">
                  <span>Carla</span>
                </span>
                <span className="absolute top-[55%] right-[20%] flex items-center gap-1.5 bg-blue-500 rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow">
                  <span>Indraja</span>
                </span>
                <span className="absolute bottom-[10%] left-[62%] flex items-center gap-1.5 bg-gray-500 rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow">
                  <span>Stephen</span>
                </span>
              </div>
            </div>
          </div>

          {/* CARD 2: Express yourself */}
          <div className="rounded-2xl border border-zinc-200/70 p-6 bg-white shadow-sm flex flex-col justify-between">
            <div className="space-y-2 mb-6">
              <h3 className="text-lg font-bold font-sans text-zinc-900">
                Express yourself
              </h3>
              <p className="text-[13px] text-zinc-600 leading-relaxed">
                React to ideas with stamps, emotes, and high fives for quick responses in real time.
              </p>
            </div>

            {/* Option 1 & Option 2 buttons inside beautiful illustration */}
            <div className="bg-[#FAF5FF] border border-zinc-100 rounded-xl p-4 h-48 flex flex-col justify-center items-center relative overflow-hidden">
              <div className="grid grid-cols-2 gap-4 w-full max-w-xs z-10">
                
                {/* Option 1 Box */}
                <div className="border border-zinc-200 bg-white shadow rounded-lg p-3 text-center">
                  <span className="text-[10px] uppercase font-bold text-zinc-400 block mb-1">Option 1</span>
                  <div className="flex items-center justify-center gap-1.5 mt-1">
                    <button 
                      onClick={(e) => triggerReactionParticles(e, 'heart', 'optA')}
                      className="rounded-full bg-zinc-50 p-1 hover:bg-pink-100 hover:scale-110 active:scale-95 transition-all text-sm shadow-sm"
                    >
                      ❤️
                    </button>
                    <span className="text-xs font-extrabold text-zinc-700">{feedbackVote.optA}</span>
                  </div>
                </div>

                {/* Option 2 Box */}
                <div className="border border-zinc-200 bg-white shadow rounded-lg p-3 text-center">
                  <span className="text-[10px] uppercase font-bold text-zinc-400 block mb-1">Option 2</span>
                  <div className="flex items-center justify-center gap-1.5 mt-1">
                    <button 
                      onClick={(e) => triggerReactionParticles(e, 'thumb', 'optB')}
                      className="rounded-full bg-zinc-50 p-1 hover:bg-blue-100 hover:scale-110 active:scale-95 transition-all text-sm shadow-sm"
                    >
                      👍
                    </button>
                    <span className="text-xs font-extrabold text-zinc-700">{feedbackVote.optB}</span>
                  </div>
                </div>

              </div>

              {/* Floating emoji particles layer */}
              <AnimatePresence>
                {activeBounces.map(b => (
                  <motion.div 
                    key={b.id}
                    initial={{ y: 20, opacity: 0, scale: 0.5 }}
                    animate={{ y: -70, opacity: [0, 1, 1, 0], scale: 1.6 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.2, ease: 'easeOut' }}
                    className="absolute text-xl pointer-events-none z-20"
                  >
                    {b.emoji}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* CARD 3: Give everyone a say */}
          <div className="rounded-2xl border border-zinc-200/70 p-6 bg-white shadow-sm flex flex-col justify-between">
            <div className="space-y-2 mb-6">
              <h3 className="text-lg font-bold font-sans text-zinc-900">
                Give everyone a say
              </h3>
              <p className="text-[13px] text-zinc-600 leading-relaxed">
                Invite people from other teams or companies to contribute to your files free for 24 hours—no login required.
              </p>
            </div>

            {/* Displaying moving roles tags on dark blueprint grid */}
            <div className="bg-zinc-900 rounded-xl p-4 h-48 relative overflow-hidden flex items-center justify-center border border-zinc-800">
              <div className="absolute inset-0 opacity-10 dot-grid"></div>

              <span className="bg-[#19BCFE] rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow absolute top-5 left-4 animate-bounce">
                <span>Guest</span>
              </span>
              <span className="bg-[#A259FF] rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow absolute top-4 right-14">
                <span>Designer</span>
              </span>
              <span className="bg-[#4CAF50] rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow absolute top-12 right-4">
                <span>Developer</span>
              </span>
              <span className="bg-[#FF9800] rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow absolute bottom-5 left-6">
                <span>Marketer</span>
              </span>
              <span className="bg-pink-500 rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow absolute bottom-12 right-20">
                <span>Product manager</span>
              </span>
              <span className="bg-[#E57373] rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow absolute bottom-4 right-4">
                <span>Producer</span>
              </span>
              <span className="bg-teal-500 rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white shadow absolute top-[44%] left-[38%]">
                <span>Researcher</span>
              </span>

              <span className="text-[10px] font-mono font-bold text-zinc-500 tracking-wider uppercase z-10 bg-zinc-950/40 px-2 py-1 rounded">Interactive Guest Canvas</span>
            </div>
          </div>

          {/* CARD 4: Facilitate dynamic conversations (Spotlight mode mockup) */}
          <div className="rounded-2xl border border-zinc-200/70 p-6 bg-white shadow-sm flex flex-col justify-between" id="spotlight-card">
            <div className="space-y-2 mb-6">
              <h3 className="text-lg font-bold font-sans text-zinc-900">
                Facilitate dynamic conversations
              </h3>
              <p className="text-[13px] text-zinc-600 leading-relaxed">
                Guide meeting participants to focus on content that matters most with Spotlight mode. Toggling it captures attention!
              </p>
            </div>

            {/* Custom interactive spotlight component */}
            <div className="bg-zinc-50 border border-zinc-100 rounded-xl p-4 h-48 flex flex-col justify-between relative overflow-hidden">
              {/* Workspace dynamic toolbar */}
              <div className="flex items-center justify-between bg-white border border-zinc-200 shadow-sm rounded-full px-3 py-1.5 w-full">
                <div className="flex items-center gap-1">
                  <span className="text-sm">🎧</span>
                  <div className="flex -space-x-1.5">
                    <div className="h-5 w-5 bg-purple-500 text-white font-bold text-[7px] rounded-full flex items-center justify-center">A</div>
                    <div className="h-5 w-5 bg-emerald-500 text-white font-bold text-[7px] rounded-full flex items-center justify-center">B</div>
                    <div className="h-5 w-5 bg-yellow-500 text-white font-bold text-[7px] rounded-full flex items-center justify-center">C</div>
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <button 
                    onClick={() => setSpotlightActive(!spotlightActive)}
                    className="bg-[#A259FF] text-white rounded-full text-[9px] font-bold px-3 py-1 hover:bg-[#8e44ad] transition-colors"
                  >
                    Share
                  </button>
                  <MessageSquare size={12} className="text-zinc-500" />
                </div>
              </div>

              {/* Spotlight focus button */}
              <div className="flex flex-col items-center justify-center flex-1 py-1">
                <div className="bg-white border-2 border-[#A259FF] rounded-lg p-3 text-center shadow-sm w-36 relative">
                  <span className="font-bold text-[9px] block text-zinc-500 uppercase">Jessica (you)</span>
                  <button 
                    onClick={() => setSpotlightActive(!spotlightActive)}
                    className="mt-1 bg-[#A259FF] text-[9px] font-bold text-white rounded px-2.5 py-1 uppercase block w-full hover:bg-purple-700 hover:scale-105 active:scale-95 transition-all shadow-sm"
                  >
                    {spotlightActive ? 'Spotlight active ●' : 'Spotlight me'}
                  </button>
                </div>
              </div>

              {/* Dynamic mouse movement tracking window */}
              {spotlightActive && (
                <div 
                  onMouseMove={handleSpotlightMouseMove}
                  className="absolute inset-0 bg-zinc-950/85 z-20 flex flex-col items-center justify-center cursor-none p-4"
                >
                  <div 
                    style={{ left: spotlightCoords.x - 6, top: spotlightCoords.y - 6 }}
                    className="absolute h-3.5 w-3.5 bg-white rounded-full pointer-events-none shadow"
                  />
                  <div 
                    style={{
                      background: `radial-gradient(circle 50px at ${spotlightCoords.x}px ${spotlightCoords.y}px, transparent 100%, rgba(9, 9, 11, 0.95) 100%)`
                    }}
                    className="absolute inset-0 pointer-events-none"
                  />
                  <span className="text-[10px] font-bold text-white z-10 text-center uppercase tracking-wider backdrop-blur-sm bg-black/30 p-1.5 rounded">
                    Spotlight Mode Active! Move cursor inside card.
                  </span>
                </div>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 5: iPad Section (Peach Background) */}
      <section className="rounded-3xl bg-[#FFC8A3]/25 border border-[#FFC8A3]/45 p-8 md:p-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="space-y-4">
            <h2 className="font-sans text-3xl font-extrabold text-zinc-900 tracking-tight leading-tight">
              Sketch out and explore ideas on iPad
            </h2>
            <p className="text-[14.5px] text-zinc-650 leading-relaxed max-w-md font-medium">
              Use the FigJam app to gather inspiration and riff on early ideas solo—then collaborate with your teammates to make them real.
            </p>
            <div className="pt-3 flex flex-wrap gap-4 items-center">
              <a href="#whiteboard-arena" className="rounded-lg bg-zinc-900 hover:bg-black text-white text-xs font-bold px-5 py-3 transition-colors shadow">
                Download the app
              </a>
              <a href="#whiteboard-arena" className="text-xs font-bold text-zinc-800 hover:text-black flex items-center gap-1">
                Learn more <ChevronRight size={14} />
              </a>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white rounded-2xl shadow-md p-6 h-52 flex items-center justify-center relative overflow-hidden">
            <Smartphone size={60} className="text-zinc-300 absolute left-8 rotate-12" />
            <div className="absolute right-12 text-zinc-400 font-mono text-[11px] font-bold uppercase tracking-widest text-right">
              Stylus & Touch<br />
              Optimization Live
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: Create workflow that works for you (Green background) */}
      <section className="rounded-3xl bg-[#C2F5D7]/25 border border-[#C2F5D7]/45 p-8 md:p-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="space-y-4 order-last md:order-first">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-zinc-200/80 shadow-sm p-4 rounded-xl flex items-center justify-center hover:scale-[1.04] transition-all cursor-pointer">
                <span className="font-extrabold text-zinc-800 text-xs">Jira</span>
              </div>
              <div className="bg-white border border-zinc-200 shadow-sm p-4 rounded-xl flex items-center justify-center hover:scale-[1.04] transition-all cursor-pointer">
                <span className="font-extrabold text-zinc-800 text-xs text-red-500">Asana</span>
              </div>
              <div className="bg-white border border-zinc-200 shadow-sm p-4 rounded-xl flex items-center justify-center hover:scale-[1.04] transition-all cursor-pointer">
                <span className="font-extrabold text-zinc-800 text-xs text-purple-600">GitHub</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="font-sans text-3xl font-extrabold text-zinc-900 tracking-tight leading-tight">
              Create a workflow that works for you
            </h2>
            <p className="text-[14.5px] text-zinc-650 leading-relaxed font-semibold">
              Whether you're breaking the ice or planning a project, FigJam has all of the integrations, widgets, and plugins your team needs to move work forward.
            </p>
            <a href="#whiteboard-arena" className="text-xs font-bold text-emerald-800 hover:text-black flex items-center gap-1 pt-2">
              Explore widgets <ChevronRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 7 (NEW): "Turn ideas into action" */}
      <section className="rounded-3xl border border-zinc-200/50 p-8 md:p-14 bg-[#FAF9FB]">
        <div className="max-w-2xl space-y-4">
          <h2 className="font-sans text-3xl font-extrabold text-zinc-900 tracking-tight leading-tight">
            Turn ideas into action
          </h2>
          <p className="text-[14.5px] text-zinc-600 leading-relaxed font-semibold">
            FigJam integrates with <span className="text-red-500 font-bold">Asana</span>, <span className="text-blue-500 font-bold">Jira</span>, and <span className="text-purple-600 font-bold">Github</span>, so you can keep projects moving after the brainstorm ends.
          </p>
          <div className="pt-2">
            <a href="#whiteboard-arena" className="text-xs font-bold text-zinc-800 hover:text-black flex items-center gap-1">
              Explore integrations <ChevronRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 8 (NEW): "Show some personality" */}
      <section className="rounded-3xl border border-zinc-200/50 p-8 md:p-14 bg-white grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7 space-y-4">
          <h2 className="font-sans text-3xl font-extrabold text-zinc-900 tracking-tight leading-tight">
            Show some personality
          </h2>
          <p className="text-[14.5px] text-zinc-600 leading-relaxed font-semibold animate-pulse">
            Add a personal touch to your FigJam files with customized Bitmoji avatars. Let your teammates see your unique expression!
          </p>
        </div>
        <div className="md:col-span-5 flex items-center gap-4 justify-center">
          <div className="h-14 w-14 rounded-full bg-yellow-105 border-4 border-yellow-200 flex items-center justify-center font-bold text-lg shadow-sm">👦🏽</div>
          <div className="h-14 w-14 rounded-full bg-purple-105 border-4 border-purple-200 flex items-center justify-center font-bold text-lg shadow-sm">👧🏻</div>
          <div className="h-14 w-14 rounded-full bg-emerald-110 border-4 border-emerald-200 flex items-center justify-center font-bold text-lg shadow-sm">👦🏼</div>
        </div>
      </section>

    </div>
  );
}






interface WhiteboardProps {
  initialTemplate?: string;
  onTemplateSelected?: () => void;
}

// Storage Keys
const STORAGE_STICKIES_KEY = 'figjam_clone_stickies';
const STORAGE_SHAPES_KEY = 'figjam_clone_shapes';
const STORAGE_CONNECTIONS_KEY = 'figjam_clone_connections';
const STORAGE_PATHS_KEY = 'figjam_clone_paths';

// Pre-defined color codes
const COLOR_MAP: Record<StickyColor, { bg: string; text: string; label: string; border: string }> = {
  yellow: { bg: '#FFF1AC', text: '#5F4D00', label: 'Yellow', border: '#EBD268' },
  pink: { bg: '#FFCCD9', text: '#7E1F3C', label: 'Pink', border: '#FFA8BF' },
  orange: { bg: '#FFC8A3', text: '#803A0C', label: 'Orange', border: '#FCA46D' },
  blue: { bg: '#B3E4FF', text: '#004770', label: 'Blue', border: '#84CEFA' },
  green: { bg: '#C2F5D7', text: '#0A552C', label: 'Green', border: '#92EAA8' },
  red: { bg: '#FFBDBC', text: '#851C1A', label: 'Red', border: '#FF9493' },
};

const STAMP_EMOJIS: Record<StampType, string> = {
  heart: '❤️',
  thumb: '👍',
  star: '⭐',
  fire: '🔥',
  smiley: '😊'
};

const SHAPE_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  rectangle: { bg: '#FFFFFF', border: '#2C2C2C', text: '#1E1E1E' },
  circle: { bg: '#F3E8FF', border: '#A855F7', text: '#1E1E1E' },
  diamond: { bg: '#FEF3C7', border: '#D97706', text: '#1E1E1E' },
};

// Preset Teams Collaboration Simulation Data
const SIMULATED_PEERS: PeerCursor[] = [
  { id: 'peer-1', name: 'Himari', color: '#ec4899', x: 260, y: 350, role: 'Product' },
  { id: 'peer-2', name: 'Fiona', color: '#3b82f6', x: 540, y: 180, role: 'Design' },
  { id: 'peer-3', name: 'Deepak', color: '#10b981', x: 190, y: 550, role: 'Engineering' },
  { id: 'peer-4', name: 'Sydney', color: '#f59e0b', x: 310, y: 220, role: 'UX Research' },
];

function Whiteboard({ 
  initialTemplate, 
  onTemplateSelected 
}: WhiteboardProps) {

  // Local unique user ID for tab syncing
  const userId = useMemo(() => 'user-' + Math.random().toString(36).substr(2, 9), []);

  const containerRef = useRef<HTMLDivElement>(null);
  const drawingSvgRef = useRef<SVGSVGElement>(null);

  // Tools State
  const [tool, setTool] = useState<ToolType>('select');
  const [stickyColor, setStickyColor] = useState<StickyColor>('yellow');
  const [shapeType, setShapeType] = useState<ShapeType>('rectangle');
  const [stampType, setStampType] = useState<StampType>('heart');

  // Canvas State & elements
  const [stickies, setStickies] = useState<StickyNote[]>([]);
  const [shapes, setShapes] = useState<ShapeElement[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [paths, setPaths] = useState<DrawPath[]>([]);
  const [stamps, setStamps] = useState<StampInstance[]>([]);

  // Selection states
  const [connectionStartId, setConnectionStartId] = useState<string | null>(null);

  // Dragging / Pan Coordinates
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [draggedType, setDraggedType] = useState<'sticky' | 'shape' | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Current drawing line
  const [currentPathPoints, setCurrentPathPoints] = useState<{ x: number; y: number }[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);

  // Real-time peer cursors (including other actual tabs)
  const [peers, setPeers] = useState<PeerCursor[]>(SIMULATED_PEERS);
  const [localCursor, setLocalCursor] = useState({ x: 0, y: 0 });

  // Widget States (Timer, Music, Voting)
  const [timeLeft, setTimeLeft] = useState(292); // 4 minutes 52 seconds (matching screenshot "04:52")
  const [timerRunning, setTimerRunning] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [voteCount, setVoteCount] = useState<Record<string, number>>({ A: 12, B: 7 });

  // Audio Context for royalty-free lofi synthesis beats
  const audioContextRef = useRef<AudioContext | null>(null);
  const lofiOscRef = useRef<OscillatorNode | null>(null);
  const lofiGainRef = useRef<GainNode | null>(null);

  // Load Initial coordinates / set seed of defaults
  useEffect(() => {
    try {
      const savedStickies = localStorage.getItem(STORAGE_STICKIES_KEY);
      const savedShapes = localStorage.getItem(STORAGE_SHAPES_KEY);
      const savedConnections = localStorage.getItem(STORAGE_CONNECTIONS_KEY);
      const savedPaths = localStorage.getItem(STORAGE_PATHS_KEY);

      if (savedStickies && savedShapes) {
        setStickies(JSON.parse(savedStickies));
        setShapes(JSON.parse(savedShapes));
        setConnections(savedConnections ? JSON.parse(savedConnections) : []);
        setPaths(savedPaths ? JSON.parse(savedPaths) : []);
      } else {
        // Seed standard Figma screenshot representation
        loadDemoSeed();
      }
    } catch (e) {
      loadDemoSeed();
    }
  }, []);

  // Sync to localstorage when elements mutate
  useEffect(() => {
    if (stickies.length > 0) localStorage.setItem(STORAGE_STICKIES_KEY, JSON.stringify(stickies));
  }, [stickies]);

  useEffect(() => {
    if (shapes.length > 0) localStorage.setItem(STORAGE_SHAPES_KEY, JSON.stringify(shapes));
  }, [shapes]);

  useEffect(() => {
    localStorage.setItem(STORAGE_CONNECTIONS_KEY, JSON.stringify(connections));
  }, [connections]);

  useEffect(() => {
    localStorage.setItem(STORAGE_PATHS_KEY, JSON.stringify(paths));
  }, [paths]);

  // Load seeded demo structure matching the Figma screenshot exactly
  const loadDemoSeed = () => {
    const defaultStickies: StickyNote[] = [
      { id: 'st-1', x: 80, y: 160, width: 170, height: 160, text: 'Introduce stats and achievements', color: 'orange', rotation: -3 },
      { id: 'st-2', x: 270, y: 150, width: 170, height: 160, text: 'Thoughtful privacy settings', color: 'yellow', rotation: 2 },
      { id: 'st-3', x: 80, y: 350, width: 170, height: 160, text: 'Add collection functionality', color: 'orange', rotation: 1 },
      { id: 'st-4', x: 270, y: 340, width: 170, height: 160, text: 'Import saved recipes and favorites from other apps', color: 'yellow', rotation: -2 },
      { id: 'st-5', x: 460, y: 340, width: 170, height: 160, text: 'Highlight shared favs', color: 'yellow', rotation: 3 },
      { id: 'st-6', x: 740, y: 220, width: 160, height: 150, text: 'More AI-driven recaps', color: 'pink', rotation: -1 },
      { id: 'st-7', x: 920, y: 210, width: 160, height: 150, text: 'Mid-project reviews are so helpful!', color: 'yellow', rotation: 3 },
    ];

    const defaultShapes: ShapeElement[] = [
      { id: 'sh-1', type: 'rectangle', x: 80, y: 50, width: 550, height: 65, text: 'HOW MIGHT WE IMPROVE USER PROFILES?', color: '#F9FAFB', borderColor: '#E4E4E7' },
      { id: 'sh-2', type: 'rectangle', x: 740, y: 50, width: 340, height: 65, text: 'HOW MIGHT WE IMPROVE OUR PROCESS?', color: '#F9FAFB', borderColor: '#E4E4E7' },
    ];

    const defaultConnections: Connection[] = [
      { id: 'cn-1', fromId: 'sh-1', toId: 'st-2' },
      { id: 'cn-2', fromId: 'sh-2', toId: 'st-6' },
    ];

    setStickies(defaultStickies);
    setShapes(defaultShapes);
    setConnections(defaultConnections);
    setPaths([]);
    setStamps([]);
  };

  // Cross-tab tracking logic
  useEffect(() => {
    const handleStorageUpdate = (e: StorageEvent) => {
      try {
        if (e.key === STORAGE_STICKIES_KEY && e.newValue) setStickies(JSON.parse(e.newValue));
        if (e.key === STORAGE_SHAPES_KEY && e.newValue) setShapes(JSON.parse(e.newValue));
        if (e.key === STORAGE_CONNECTIONS_KEY && e.newValue) setConnections(JSON.parse(e.newValue));
        if (e.key === STORAGE_PATHS_KEY && e.newValue) setPaths(JSON.parse(e.newValue));

        // Scan other cursors
        if (e.key && e.key.startsWith('figjam_cursor_') && e.newValue) {
          const remoteCursor = JSON.parse(e.newValue);
          if (remoteCursor.userId !== userId) {
            setPeers(prev => {
              const base = prev.filter(p => p.id !== remoteCursor.userId);
              return [...base, {
                id: remoteCursor.userId,
                name: remoteCursor.name || 'Peer Tab',
                color: remoteCursor.color,
                x: remoteCursor.x,
                y: remoteCursor.y,
                role: 'Contributor'
              }];
            });
          }
        }
      } catch (err) {}
    };

    window.addEventListener('storage', handleStorageUpdate);
    return () => window.removeEventListener('storage', handleStorageUpdate);
  }, [userId]);

  // Handle external templates injection
  useEffect(() => {
    if (initialTemplate) {
      injectTemplate(initialTemplate);
      if (onTemplateSelected) onTemplateSelected();
    }
  }, [initialTemplate]);

  // Teammates cursor noise simulation loop
  useEffect(() => {
    const interval = setInterval(() => {
      setPeers(prev => prev.map(peer => {
        // Only update simulated static peers on interval
        if (!peer.id.startsWith('peer-')) return peer;

        // Move cursor smoothly towards random slight offset or a sticky note
        const destOffset = 40;
        const targetSticky = stickies[Math.floor(Math.random() * stickies.length)];
        
        let newX = peer.x + (Math.random() * 60 - 30);
        let newY = peer.y + (Math.random() * 60 - 30);

        // Keep inside reasonable dimensions
        if (newX < 50) newX = 200;
        if (newX > 1100) newX = 900;
        if (newY < 50) newY = 150;
        if (newY > 600) newY = 400;

        // Random simulated sticky text changes half-jokingly (occasional visual fun)
        const peerMessage = Math.random() > 0.85 
          ? [
              "Love this!", 
              "Approved!", 
              "Wait, what about security?", 
              "Let's move this", 
              "Nice template",
              "Who drew this? 😂"
            ][Math.floor(Math.random() * 6)] 
          : undefined;

        // Drop a random stamp occasionally
        if (Math.random() > 0.94) {
          const stampTypes: StampType[] = ['heart', 'thumb', 'star', 'fire', 'smiley'];
          const randomStamp = stampTypes[Math.floor(Math.random() * stampTypes.length)];
          setStamps(curr => [
            ...curr,
            {
              id: 'stmp-' + Math.random(),
              type: randomStamp,
              x: peer.x,
              y: peer.y,
              timestamp: Date.now()
            }
          ]);
        }

        return {
          ...peer,
          x: Math.round(newX),
          y: Math.round(newY),
          message: peerMessage
        };
      }));
    }, 2800);

    return () => clearInterval(interval);
  }, [stickies]);

  // Timer countdown loop
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timerRunning && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(t => t - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setTimerRunning(false);
    }
    return () => clearInterval(timer);
  }, [timerRunning, timeLeft]);

  // Synthesis of royalty free Lo-Fi Beats audio generator
  const toggleLofiBeats = () => {
    if (musicPlaying) {
      // stop lofi
      try {
        if (lofiOscRef.current) {
          lofiOscRef.current.stop();
          lofiOscRef.current.disconnect();
        }
      } catch (e) {}
      setMusicPlaying(false);
    } else {
      // start synthesis of sweet pulsing sine wave chord loop
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = new AudioContextClass();
        audioContextRef.current = ctx;

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        // Relaxing low note (110Hz or 146.8Hz - D3 chord)
        osc.frequency.setValueAtTime(146.83, ctx.currentTime);

        // Low volume
        gain.gain.setValueAtTime(isMuted ? 0 : 0.08, ctx.currentTime);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();

        lofiOscRef.current = osc;
        lofiGainRef.current = gain;
        setMusicPlaying(true);

        // Add volume swell lofi effect
        let pitchUp = true;
        const modulation = setInterval(() => {
          if (!lofiOscRef.current || ctx.state === 'closed') {
            clearInterval(modulation);
            return;
          }
          const baseFreq = pitchUp ? 146.83 : 138.59; // slide between reassuring major notes
          lofiOscRef.current.frequency.setValueAtTime(baseFreq, ctx.currentTime);
          pitchUp = !pitchUp;
        }, 4000);

      } catch (err) {
        console.warn("Audio Context init blocked or failed: ", err);
        setMusicPlaying(true); // show state anyway for interface feel
      }
    }
  };

  const toggleMute = () => {
    if (lofiGainRef.current && audioContextRef.current) {
      lofiGainRef.current.gain.setValueAtTime(isMuted ? 0.08 : 0, audioContextRef.current.currentTime);
    }
    setIsMuted(!isMuted);
  };

  // Convert timer format to MM:SS
  const formatTimer = (secs: number) => {
    const mins = Math.floor(secs / 60);
    const remaining = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${remaining.toString().padStart(2, '0')}`;
  };

  // Coordinates helper
  const getCanvasCoords = (e: React.MouseEvent) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    // Offset by panning position
    return {
      x: e.clientX - rect.left - panOffset.x,
      y: e.clientY - rect.top - panOffset.y,
    };
  };

  // Canvas Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    const coords = getCanvasCoords(e);

    // Save local mouse coordinates for cross-tab sharing
    setLocalCursor(coords);
    localStorage.setItem(`figjam_cursor_${userId}`, JSON.stringify({
      userId,
      x: coords.x,
      y: coords.y,
      color: '#9333ea',
      name: 'Host tab'
    }));

    if (tool === 'pan') {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      return;
    }

    if (tool === 'draw') {
      setIsDrawing(true);
      setCurrentPathPoints([{ x: coords.x, y: coords.y }]);
      return;
    }

    if (tool === 'sticky') {
      // Place high-end sticky with slight rotation
      const newSticky: StickyNote = {
        id: 'st-' + Date.now(),
        x: coords.x - 85,
        y: coords.y - 80,
        width: 170,
        height: 160,
        text: 'New Sticky note\n(double click to edit)',
        color: stickyColor,
        rotation: Math.floor(Math.random() * 6) - 3 // slants slightly between -3deg and +3deg
      };
      setStickies(prev => [...prev, newSticky]);
      setTool('select');
      return;
    }

    if (tool === 'shape') {
      const colors = {
        rectangle: '#FFFFFF',
        circle: '#F3E8FF',
        diamond: '#FEF3C7',
      };
      const borderColors = {
        rectangle: '#2C2C2C',
        circle: '#A855F7',
        diamond: '#D97706',
      };

      const newShape: ShapeElement = {
        id: 'sh-' + Date.now(),
        type: shapeType,
        x: coords.x - 90,
        y: coords.y - 45,
        width: 180,
        height: 90,
        text: 'Shape box',
        color: colors[shapeType],
        borderColor: borderColors[shapeType],
      };
      setShapes(prev => [...prev, newShape]);
      setTool('select');
      return;
    }

    if (tool === 'stamp') {
      const newStamp: StampInstance = {
        id: 'stmp-' + Date.now(),
        type: stampType,
        x: coords.x,
        y: coords.y,
        timestamp: Date.now()
      };
      setStamps(prev => [...prev, newStamp]);
      
      // Clean up stamps after 5 seconds to avoid over-cluttering canvas state
      setTimeout(() => {
        setStamps(curr => curr.filter(s => s.id !== newStamp.id));
      }, 5000);
      return;
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const coords = getCanvasCoords(e);
    setLocalCursor(coords);

    // Share cursor live with localstorage tabs
    try {
      localStorage.setItem(`figjam_cursor_${userId}`, JSON.stringify({
        userId,
        x: coords.x,
        y: coords.y,
        color: '#9333ea',
        name: 'You'
      }));
    } catch (err) {}

    if (isPanning && tool === 'pan') {
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
      return;
    }

    if (isDrawing && tool === 'draw') {
      setCurrentPathPoints(prev => [...prev, { x: coords.x, y: coords.y }]);
      return;
    }

    if (draggedId) {
      if (draggedType === 'sticky') {
        setStickies(prev => prev.map(s => {
          if (s.id === draggedId) {
            return { ...s, x: coords.x - dragOffset.x, y: coords.y - dragOffset.y };
          }
          return s;
        }));
      } else if (draggedType === 'shape') {
        setShapes(prev => prev.map(sh => {
          if (sh.id === draggedId) {
            return { ...sh, x: coords.x - dragOffset.x, y: coords.y - dragOffset.y };
          }
          return sh;
        }));
      }
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    setDraggedId(null);
    setDraggedType(null);

    if (isDrawing && tool === 'draw' && currentPathPoints.length > 1) {
      const newPath: DrawPath = {
        id: 'pth-' + Date.now(),
        points: currentPathPoints,
        color: stickyColor === 'yellow' ? '#1E1E1E' : COLOR_MAP[stickyColor].text,
        width: 3.5,
      };
      setPaths(prev => [...prev, newPath]);
      setCurrentPathPoints([]);
      setIsDrawing(false);
    }
  };

  // Drag handles for elements
  const startDrag = (e: React.MouseEvent, id: string, type: 'sticky' | 'shape') => {
    e.stopPropagation();
    if (tool !== 'select') return;

    const coords = getCanvasCoords(e);
    let targetX = 0, targetY = 0;

    if (type === 'sticky') {
      const activeObj = stickies.find(s => s.id === id);
      if (activeObj) {
        targetX = activeObj.x;
        targetY = activeObj.y;
      }
    } else {
      const activeObj = shapes.find(s => s.id === id);
      if (activeObj) {
        targetX = activeObj.x;
        targetY = activeObj.y;
      }
    }

    setDraggedId(id);
    setDraggedType(type);
    setDragOffset({
      x: coords.x - targetX,
      y: coords.y - targetY,
    });
  };

  // Node text modifications
  const handleTextChange = (id: string, text: string, type: 'sticky' | 'shape') => {
    if (type === 'sticky') {
      setStickies(prev => prev.map(s => s.id === id ? { ...s, text } : s));
    } else {
      setShapes(prev => prev.map(s => s.id === id ? { ...s, text } : s));
    }
  };

  // Node deletions (Eraser)
  const handleItemInteract = (id: string, type: 'sticky' | 'shape') => {
    if (tool === 'eraser') {
      if (type === 'sticky') {
        setStickies(prev => prev.filter(s => s.id !== id));
      } else {
        setShapes(prev => prev.filter(sh => sh.id !== id));
      }
      setConnections(curr => curr.filter(c => c.fromId !== id && c.toId !== id));
      return;
    }

    if (tool === 'connection') {
      if (!connectionStartId) {
        setConnectionStartId(id);
      } else {
        if (connectionStartId !== id) {
          const newConn: Connection = {
            id: 'conn-' + Date.now(),
            fromId: connectionStartId,
            toId: id,
          };
          setConnections(prev => [...prev, newConn]);
        }
        setConnectionStartId(null);
        setTool('select');
      }
    }
  };

  // Template loaders
  const injectTemplate = (type: string) => {
    let freshStickies: StickyNote[] = [];
    let freshShapes: ShapeElement[] = [];
    let freshConns: Connection[] = [];

    if (type === 'kickoff') {
      freshShapes = [
        { id: 'sh-k1', type: 'rectangle', x: 100, y: 60, width: 280, height: 60, text: '📋 ASSIGNMENTS & GOALS', color: '#DDBBFF', borderColor: '#A855F7' },
        { id: 'sh-k2', type: 'rectangle', x: 420, y: 60, width: 280, height: 60, text: '💡 INSPIRATION / MOODBOARD', color: '#FFF1AC', borderColor: '#EBD268' },
        { id: 'sh-k3', type: 'rectangle', x: 740, y: 60, width: 280, height: 60, text: '🎯 TARGET ASSIGNMENTS', color: '#C2F5D7', borderColor: '#92EAA8' },
      ];
      freshStickies = [
        { id: 'st-k1', x: 100, y: 150, width: 130, height: 130, text: 'Assign lead developer', color: 'pink', rotation: -1 },
        { id: 'st-k2', x: 240, y: 150, width: 130, height: 130, text: 'Establish MVP parameters', color: 'pink', rotation: 2 },
        { id: 'st-k3', x: 420, y: 150, width: 130, height: 130, text: 'Competitor benchmarking layout', color: 'blue', rotation: -2 },
        { id: 'st-k4', x: 560, y: 150, width: 130, height: 130, text: 'Warm, human typography mood', color: 'yellow', rotation: 1 },
        { id: 'st-k5', x: 740, y: 150, width: 130, height: 130, text: 'Draft Figma layout skeleton', color: 'green', rotation: 3 },
      ];
    } else if (type === 'flowchart') {
      freshShapes = [
        { id: 'sh-f1', type: 'circle', x: 150, y: 180, width: 110, height: 110, text: 'Start App', color: '#C2F5D7', borderColor: '#1ABC9C' },
        { id: 'sh-f2', type: 'rectangle', x: 340, y: 190, width: 160, height: 90, text: 'OAuth Verification Check', color: '#FFFFFF', borderColor: '#E4E4E7' },
        { id: 'sh-f3', type: 'diamond', x: 570, y: 165, width: 140, height: 140, text: 'Is Key\nConfigured?', color: '#FFEBEB', borderColor: '#F24E1E' },
        { id: 'sh-f4', type: 'rectangle', x: 790, y: 190, width: 160, height: 90, text: 'Initialize Figma Playboard', color: '#B3E4FF', borderColor: '#3b82f6' },
      ];
      freshConns = [
        { id: 'cn-f1', fromId: 'sh-f1', toId: 'sh-f2' },
        { id: 'cn-f2', fromId: 'sh-f2', toId: 'sh-f3' },
        { id: 'cn-f3', fromId: 'sh-f3', toId: 'sh-f4', label: 'Yes' },
      ];
    } else if (type === 'mindmap') {
      freshShapes = [
        { id: 'sh-m1', type: 'circle', x: 400, y: 220, width: 180, height: 110, text: 'FigJam AI Upgrade', color: '#DDBBFF', borderColor: '#A855F7' },
      ];
      freshStickies = [
        { id: 'st-m1', x: 160, y: 110, width: 130, height: 130, text: 'Interactive Whiteboard', color: 'pink', rotation: -3 },
        { id: 'st-m2', x: 650, y: 100, width: 130, height: 130, text: 'Gemini prompt creation', color: 'yellow', rotation: 1 },
        { id: 'st-m3', x: 200, y: 350, width: 130, height: 130, text: 'Multiplayer pointers', color: 'blue', rotation: 2 },
        { id: 'st-m4', x: 610, y: 350, width: 130, height: 130, text: 'Custom Vector arrows', color: 'orange', rotation: -1 },
      ];
      freshConns = [
        { id: 'cn-m1', fromId: 'sh-m1', toId: 'st-m1' },
        { id: 'cn-m2', fromId: 'sh-m1', toId: 'st-m2' },
        { id: 'cn-m3', fromId: 'sh-m1', toId: 'st-m3' },
        { id: 'cn-m4', fromId: 'sh-m1', toId: 'st-m4' },
      ];
    } else if (type === 'retro') {
      freshShapes = [
        { id: 'sh-r1', type: 'rectangle', x: 100, y: 60, width: 240, height: 50, text: '🟢 WHAT WENT WELL', color: '#C2F5D7', borderColor: '#1ABC9C' },
        { id: 'sh-r2', type: 'rectangle', x: 400, y: 60, width: 240, height: 50, text: '🔴 WHAT TO IMPROVE', color: '#FFEBEB', borderColor: '#F24E1E' },
        { id: 'sh-r3', type: 'rectangle', x: 700, y: 60, width: 240, height: 50, text: '🟡 ACTION ITEMS', color: '#FFF8DB', borderColor: '#EBD268' },
      ];
      freshStickies = [
        { id: 'st-r1', x: 100, y: 130, width: 130, height: 130, text: 'Great team collaboration!', color: 'green', rotation: -1 },
        { id: 'st-r2', x: 100, y: 280, width: 130, height: 130, text: 'Excellent support on Slack', color: 'green', rotation: 2 },
        { id: 'st-r3', x: 400, y: 130, width: 130, height: 130, text: 'Demos were late in the evening', color: 'red', rotation: -2 },
        { id: 'st-r4', x: 400, y: 280, width: 130, height: 130, text: 'Clarify expectations earlier', color: 'red', rotation: 1 },
        { id: 'st-r5', x: 700, y: 130, width: 130, height: 130, text: 'Pre-requisites checklist for tasks', color: 'yellow', rotation: 3 },
        { id: 'st-r6', x: 700, y: 280, width: 130, height: 130, text: 'Assign secondary tester', color: 'yellow', rotation: -1 },
      ];
    } else if (type === 'journey') {
      freshShapes = [
        { id: 'sh-j1', type: 'rectangle', x: 100, y: 60, width: 220, height: 50, text: 'Phase 1: Discover', color: '#EAF8FF', borderColor: '#3b82f6' },
        { id: 'sh-j2', type: 'rectangle', x: 400, y: 60, width: 220, height: 50, text: 'Phase 2: Evaluate', color: '#FFF0FA', borderColor: '#ec4899' },
        { id: 'sh-j3', type: 'rectangle', x: 700, y: 60, width: 220, height: 50, text: 'Phase 3: Purchase', color: '#F0FDF4', borderColor: '#10b981' },
      ];
      freshStickies = [
        { id: 'st-j1', x: 100, y: 130, width: 130, height: 130, text: 'User search on Google', color: 'blue', rotation: -1 },
        { id: 'st-j2', x: 100, y: 280, width: 130, height: 130, text: 'Reads review and comparison guides', color: 'blue', rotation: 2 },
        { id: 'st-j3', x: 400, y: 130, width: 130, height: 130, text: 'Enters dynamic details in workspace', color: 'pink', rotation: -3 },
        { id: 'st-j4', x: 400, y: 280, width: 130, height: 130, text: 'Anxiety: Is there custom storage?', color: 'pink', rotation: 1 },
        { id: 'st-j5', x: 700, y: 130, width: 130, height: 130, text: 'Triggers checkout sequence form', color: 'orange', rotation: 2 },
        { id: 'st-j6', x: 700, y: 280, width: 130, height: 130, text: 'Receives onboarding link', color: 'orange', rotation: -2 },
      ];
    } else if (type === 'swot') {
      freshShapes = [
        { id: 'sh-sw1', type: 'rectangle', x: 150, y: 60, width: 240, height: 50, text: '💪 STRENGTHS (S)', color: '#C2F5D7', borderColor: '#1ABC9C' },
        { id: 'sh-sw2', type: 'rectangle', x: 450, y: 60, width: 240, height: 50, text: '⚡ WEAKNESSES (W)', color: '#FFEBEB', borderColor: '#F24E1E' },
        { id: 'sh-sw3', type: 'rectangle', x: 150, y: 280, width: 240, height: 50, text: '🌱 OPPORTUNITIES (O)', color: '#FFF8DB', borderColor: '#EBD268' },
        { id: 'sh-sw4', type: 'rectangle', x: 450, y: 280, width: 240, height: 50, text: '🔥 THREATS (T)', color: '#FFD9D9', borderColor: '#FF7262' },
      ];
      freshStickies = [
        { id: 'st-sw1', x: 150, y: 130, width: 130, height: 130, text: 'Highly visual vector canvas setup', color: 'green', rotation: -1 },
        { id: 'st-sw2', x: 450, y: 130, width: 130, height: 130, text: 'High dependency on local cache storage', color: 'red', rotation: 2 },
        { id: 'st-sw3', x: 150, y: 350, width: 130, height: 130, text: 'Integrate Gemini diagram intelligence', color: 'yellow', rotation: -2 },
        { id: 'st-sw4', x: 450, y: 350, width: 130, height: 130, text: 'Competitors copy exact features', color: 'orange', rotation: 1 },
      ];
    } else if (type === 'empathy') {
      freshShapes = [
        { id: 'sh-em1', type: 'rectangle', x: 150, y: 60, width: 240, height: 50, text: '🔊 SAYS', color: '#FFF1AC', borderColor: '#EBD268' },
        { id: 'sh-em2', type: 'rectangle', x: 450, y: 60, width: 240, height: 50, text: '💭 THINKS', color: '#B3E4FF', borderColor: '#3b82f6' },
        { id: 'sh-em3', type: 'rectangle', x: 150, y: 280, width: 240, height: 50, text: '🛠️ DOES', color: '#C2F5D7', borderColor: '#1ABC9C' },
        { id: 'sh-em4', type: 'rectangle', x: 450, y: 280, width: 240, height: 50, text: '❤️ FEELS', color: '#FFCCD9', borderColor: '#FF7262' },
      ];
      freshStickies = [
        { id: 'st-em1', x: 150, y: 130, width: 130, height: 130, text: '"I need this workflow to build instantly!"', color: 'yellow', rotation: -1 },
        { id: 'st-em2', x: 450, y: 130, width: 130, height: 130, text: 'Will my changes be persistent across pages?', color: 'blue', rotation: 2 },
        { id: 'st-em3', x: 150, y: 350, width: 130, height: 130, text: 'Draws responsive vectors, types text', color: 'green', rotation: -2 },
        { id: 'st-em4', x: 450, y: 350, width: 130, height: 130, text: 'Extremely content & fast on sandbox', color: 'pink', rotation: 1 },
      ];
    } else if (type === 'standup') {
      freshShapes = [
        { id: 'sh-su1', type: 'rectangle', x: 100, y: 60, width: 240, height: 50, text: '⏪ YESTERDAY', color: '#F3E8FF', borderColor: '#A855F7' },
        { id: 'sh-su2', type: 'rectangle', x: 400, y: 60, width: 240, height: 50, text: '⏩ TODAY', color: '#C2F5D7', borderColor: '#1ABC9C' },
        { id: 'sh-su3', type: 'rectangle', x: 700, y: 60, width: 240, height: 50, text: '🛑 BLOCKED', color: '#FFEBEB', borderColor: '#F24E1E' },
      ];
      freshStickies = [
        { id: 'st-su1', x: 100, y: 130, width: 130, height: 130, text: '[Himari] Rapped template mocks on iPad board', color: 'blue', rotation: -1 },
        { id: 'st-su2', x: 100, y: 280, width: 130, height: 130, text: '[Deepak] Connected server diagram layout logic', color: 'blue', rotation: 2 },
        { id: 'st-su3', x: 400, y: 130, width: 130, height: 130, text: '[Fiona] Cleaned layout styles & responsive viewports', color: 'green', rotation: -3 },
        { id: 'st-su4', x: 400, y: 280, width: 130, height: 130, text: '[Deepak] Match local smart fallback templates', color: 'green', rotation: 1 },
        { id: 'st-su5', x: 700, y: 130, width: 130, height: 130, text: '[Sydney] Blocking on final branding assets and assets', color: 'red', rotation: 2 },
      ];
    }

    setStickies(freshStickies);
    setShapes(freshShapes);
    setConnections(freshConns);
    setPaths([]);
    setStamps([]);
  };

  // Dynamic vector lookup for drawing arrows between objects
  const getElementCenter = (id: string) => {
    const sticky = stickies.find(s => s.id === id);
    if (sticky) {
      return { x: sticky.x + sticky.width / 2, y: sticky.y + sticky.height / 2 };
    }
    const shape = shapes.find(s => s.id === id);
    if (shape) {
      return { x: shape.x + shape.width / 2, y: shape.y + shape.height / 2 };
    }
    return null;
  };

  // Wipe the canvas and start fresh
  const clearWhiteboard = () => {
    setStickies([]);
    setShapes([]);
    setConnections([]);
    setPaths([]);
    setStamps([]);
    localStorage.removeItem(STORAGE_STICKIES_KEY);
    localStorage.removeItem(STORAGE_SHAPES_KEY);
    localStorage.removeItem(STORAGE_CONNECTIONS_KEY);
    localStorage.removeItem(STORAGE_PATHS_KEY);
  };

  return (
    <div className="relative flex flex-col overflow-hidden rounded-2xl border-4 border-zinc-900 bg-[#F9F9FB] shadow-xl md:h-[620px] h-[500px] w-full" id="whiteboard-arena">
      
      {/* Top Bar inside Canvas */}
      <div className="pointer-events-none absolute top-4 left-4 z-20 flex flex-wrap items-center gap-2.5">
        <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-1.5 shadow-sm">
          <span className="flex h-2.5 w-2.5 rounded-full bg-purple-600 animate-pulse"></span>
          <span className="font-sans text-xs font-semibold text-zinc-800">
            Whiteboard Workspace
          </span>
        </div>

        {/* Templates Quick Selector */}
        <div className="pointer-events-auto flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white p-1 shadow-sm">
          <button 
            onClick={() => injectTemplate('kickoff')}
            className="rounded-full px-2.5 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 active:bg-zinc-100 transition-colors"
          >
            Project Kickoff
          </button>
          <button 
            onClick={() => injectTemplate('flowchart')}
            className="rounded-full px-2.5 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 active:bg-zinc-100 transition-colors"
          >
            User Flow
          </button>
          <button 
            onClick={() => injectTemplate('mindmap')}
            className="rounded-full px-2.5 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 active:bg-zinc-100 transition-colors"
          >
            Mind Map
          </button>
        </div>
      </div>

      {/* Top Right Widget: Timer & Lo-fi Music */}
      <div className="absolute top-4 right-4 z-20 hidden items-center gap-2.5 sm:flex">
        <div className="rounded-2xl border border-zinc-200 bg-white p-3.5 shadow-md w-[220px]">
          <div className="mb-2 flex items-center justify-between text-xs font-semibold text-zinc-700">
            <span className="flex items-center gap-1.5 text-purple-600">
              <span className="h-1.5 w-1.5 rounded-full bg-purple-600 animate-ping"></span>
              Time, music & vote
            </span>
            <span className="font-mono text-zinc-500">Live</span>
          </div>

          {/* Countdown Clock */}
          <div className="flex items-center justify-between border-b border-zinc-100 pb-2.5 mb-2.5">
            <span className="font-mono text-2xl font-bold tracking-tight text-zinc-800">
              {formatTimer(timeLeft)}
            </span>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setTimerRunning(!timerRunning)}
                className={`rounded-full p-1.5 transition-all ${timerRunning ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'}`}
                title={timerRunning ? 'Pause timer' : 'Start countdown'}
              >
                {timerRunning ? <Pause size={13} strokeWidth={2.5} /> : <Play size={13} strokeWidth={2.5} />}
              </button>
              <button 
                onClick={() => { setTimeLeft(292); setTimerRunning(false); }}
                className="rounded-full p-1.5 bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-all"
                title="Reset timer"
              >
                <RotateCcw size={13} />
              </button>
            </div>
          </div>

          {/* Lo-fi Synth Music controller */}
          <div className="flex items-center justify-between">
            <button 
              onClick={toggleLofiBeats}
              className={`flex items-center gap-2 text-xs font-semibold rounded-lg px-2.5 py-1.5 transition-all ${musicPlaying ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'}`}
            >
              <Music size={13} className={musicPlaying ? 'animate-bounce' : ''} />
              {musicPlaying ? 'Lofi Audio: On' : 'Play Lofi chord'}
            </button>

            {musicPlaying && (
              <button onClick={toggleMute} className="p-1.5 text-zinc-700 hover:bg-zinc-100 rounded-full transition-all">
                {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Canvas interactive workspace container */}
      <div 
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        className={`dot-grid relative h-full w-full overflow-hidden select-none cursor-crosshair ${tool === 'select' ? 'cursor-default' : ''} ${tool === 'pan' ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
      >
        
        {/* Transform Group representing pan position */}
        <div style={{ transform: `translate(${panOffset.x}px, ${panOffset.y}px)` }}>
          
          {/* Vector connection lines layer */}
          <svg className="absolute top-0 left-0 h-[4000px] w-[4000px] pointer-events-none z-0 overflow-visible">
            <defs>
              <marker id="arrow" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#1E1E1E" />
              </marker>
            </defs>

            {/* Render established connecting lines */}
            {connections.map(conn => {
              const start = getElementCenter(conn.fromId);
              const end = getElementCenter(conn.toId);
              if (!start || !end) return null;

              return (
                <g key={conn.id}>
                  <line 
                    x1={start.x} 
                    y1={start.y} 
                    x2={end.x} 
                    y2={end.y} 
                    stroke="#1E1E1E" 
                    strokeWidth="2.5" 
                    markerEnd="url(#arrow)"
                    className="stroke-zinc-800"
                  />
                  {conn.label && (
                    <foreignObject 
                      x={(start.x + end.x) / 2 - 20} 
                      y={(start.y + end.y) / 2 - 12} 
                      width="40" 
                      height="24"
                    >
                      <div className="flex items-center justify-center rounded bg-zinc-900 px-1 py-0.5 text-[10px] font-bold text-white shadow">
                        {conn.label}
                      </div>
                    </foreignObject>
                  )}
                </g>
              );
            })}

            {/* Currently forming connection line preview */}
            {connectionStartId && (() => {
              const start = getElementCenter(connectionStartId);
              if (!start) return null;
              return (
                <line 
                  x1={start.x} 
                  y1={start.y} 
                  x2={localCursor.x} 
                  y2={localCursor.y} 
                  stroke="#A855F7" 
                  strokeWidth="2" 
                  strokeDasharray="4 4"
                  markerEnd="url(#arrow)"
                />
              );
            })()}

            {/* Freehand drawing lines */}
            {paths.map(p => {
              if (p.points.length < 2) return null;
              const dStr = `M ${p.points[0].x} ${p.points[0].y} ` + 
                p.points.slice(1).map(pt => `L ${pt.x} ${pt.y}`).join(' ');
              
              return (
                <path 
                  key={p.id} 
                  d={dStr} 
                  fill="none" 
                  stroke={p.color} 
                  strokeWidth={p.width} 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              );
            })}

            {/* Live active path drawing */}
            {isDrawing && currentPathPoints.length > 1 && (() => {
              const dStr = `M ${currentPathPoints[0].x} ${currentPathPoints[0].y} ` + 
                currentPathPoints.slice(1).map(pt => `L ${pt.x} ${pt.y}`).join(' ');
              
              return (
                <path 
                  d={dStr} 
                  fill="none" 
                  stroke={stickyColor === 'yellow' ? '#1E1E1E' : COLOR_MAP[stickyColor].text} 
                  strokeWidth="3.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              );
            })()}
          </svg>

          {/* DRAGGABLE GEOMETRIC SHAPES */}
          {shapes.map(sh => (
            <div 
              key={sh.id}
              onClick={() => handleItemInteract(sh.id, 'shape')}
              onMouseDown={(e) => startDrag(e, sh.id, 'shape')}
              style={{ 
                left: sh.x, 
                top: sh.y, 
                width: sh.width, 
                height: sh.height,
                borderColor: connectionStartId === sh.id ? '#A855F7' : sh.borderColor,
                backgroundColor: sh.color
              }}
              className={`absolute flex items-center justify-center border-3 p-3 shadow-md hover:shadow-lg transition-all z-10 ${tool === 'select' ? 'cursor-move active:scale-95' : 'cursor-pointer'} ${sh.type === 'circle' ? 'rounded-full' : ''} ${sh.type === 'diamond' ? 'rotate-45' : ''}`}
            >
              {/* Inner rotation offset correction for diamonds */}
              <div className={`w-full h-full flex items-center justify-center ${sh.type === 'diamond' ? '-rotate-45' : ''}`}>
                <textarea 
                  value={sh.text}
                  onChange={(e) => handleTextChange(sh.id, e.target.value, 'shape')}
                  className="w-full bg-transparent text-center font-sans text-xs font-bold text-zinc-800 border-none focus:outline-none resize-none overflow-hidden placeholder-zinc-400"
                  rows={2}
                />
              </div>
            </div>
          ))}

          {/* FIGJAM HIGHEST-FIDELITY STICKY NOTES */}
          {stickies.map(sticky => (
            <motion.div 
              key={sticky.id}
              onClick={() => handleItemInteract(sticky.id, 'sticky')}
              onMouseDown={(e) => startDrag(e, sticky.id, 'sticky')}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ 
                scale: 1, 
                opacity: 1,
                rotate: sticky.rotation || 0
              }}
              style={{ 
                left: sticky.x, 
                top: sticky.y, 
                width: sticky.width, 
                height: sticky.height,
                backgroundColor: COLOR_MAP[sticky.color].bg,
                borderColor: connectionStartId === sticky.id ? '#A855F7' : COLOR_MAP[sticky.color].border,
              }}
              className={`absolute border-2 p-3.5 shadow-sm hover:shadow-md transition-shadow z-10 transform select-text ${tool === 'select' ? 'cursor-move' : 'cursor-pointer'}`}
            >
              <textarea 
                value={sticky.text}
                onChange={(e) => handleTextChange(sticky.id, e.target.value, 'sticky')}
                className="h-full w-full bg-transparent font-sans text-[13px] font-bold border-none focus:outline-none resize-none leading-snug"
                style={{ color: COLOR_MAP[sticky.color].text }}
                placeholder="Type dynamic ideas..."
              />
              <div 
                className="absolute bottom-2 right-2 text-[10px] font-semibold opacity-40 select-none"
                style={{ color: COLOR_MAP[sticky.color].text }}
              >
                FigJam Peer
              </div>
            </motion.div>
          ))}

          {/* INTERACTIVE STAMPS / EMOJIS */}
          <AnimatePresence>
            {stamps.map(st => (
              <motion.div 
                key={st.id}
                initial={{ scale: 0.1, y: st.y + 10, opacity: 0 }}
                animate={{ scale: 1.4, y: st.y - 12, opacity: [0, 1, 1, 0.9, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
                style={{ left: st.x - 14, top: st.y - 14 }}
                className="absolute text-2xl pointer-events-none z-30"
              >
                {STAMP_EMOJIS[st.type]}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* LIVE SIMULATED PEERS CURSOR LABELS */}
          {peers.map(peer => (
            <div 
              key={peer.id}
              style={{ 
                left: peer.x, 
                top: peer.y,
                transition: 'left 0.8s cubic-bezier(0.25, 1, 0.5, 1), top 0.8s cubic-bezier(0.25, 1, 0.5, 1)'
              }}
              className="absolute pointer-events-none z-40 flex flex-col items-start"
            >
              <div className="flex items-center gap-1">
                {/* Pointer Arrow */}
                <svg className="h-5 w-5 drop-shadow-sm" viewBox="0 0 24 24" fill="none">
                  <path d="M4.5 3V17L9 12.5L14.5 18L17.5 15L12 9.5L17.5 9.5L4.5 3Z" fill={peer.color} stroke="#FFFFFF" strokeWidth="2" strokeLinejoin="miter" />
                </svg>

                {/* Name Tag */}
                <div 
                  style={{ backgroundColor: peer.color }}
                  className="rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white shadow-sm flex items-center gap-1 text-[10px]"
                >
                  <span>{peer.name}</span>
                  {peer.role && <span className="opacity-75 font-normal text-[9px]">({peer.role})</span>}
                </div>
              </div>

              {/* Speak bubble popup if peer sends direct chat */}
              {peer.message && (
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="mt-1 max-w-[140px] rounded-lg bg-zinc-900 px-2 py-1 text-[10px] font-semibold text-white shadow-md leading-tight border border-zinc-800"
                >
                  {peer.message}
                </motion.div>
              )}
            </div>
          ))}

        </div>
      </div>

      {/* Floating Cookie Settings Box from FIGJAM Screenshot */}
      <AnimatePresence>
        {timeLeft > 0 && Math.floor(timeLeft / 30) % 2 === 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute bottom-20 right-4 z-40 hidden max-w-[280px] rounded-lg bg-zinc-900 p-3 text-white shadow-xl md:block border border-zinc-800"
          >
            <div className="mb-2 text-[11px] leading-tight text-zinc-300">
              This website uses cookies, pixel tags, and local storage for collaboration features and user settings. 
              <span className="underline ml-1 cursor-pointer hover:text-white">Cookie policy</span>
            </div>
            <div className="flex flex-col gap-1">
              <button 
                onClick={() => setTimeLeft(prev => prev + 10)} 
                className="w-full rounded bg-white py-1 text-[10px] font-bold text-zinc-900 hover:bg-zinc-100 transition-colors"
              >
                Allow all cookies
              </button>
              <button 
                onClick={() => setTimeLeft(prev => prev + 5)}
                className="w-full rounded border border-zinc-700 py-1 text-[10px] font-semibold text-zinc-300 hover:bg-zinc-800 transition-colors"
              >
                Do not allow cookies
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FIGJAM BOTTOM FLOATING TOOLBAR */}
      <div className="absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-3 rounded-full border-2 border-zinc-900 bg-white px-4 py-1.5 shadow-xl select-none">
        
        {/* Hand Tool (Pan) */}
        <button 
          onClick={() => { setTool('pan'); setConnectionStartId(null); }}
          className={`rounded-full p-2.5 transition-all text-zinc-700 ${tool === 'pan' ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-100'}`}
          title="Hand / Pan tool (H)"
        >
          <Hand size={15} />
        </button>

        {/* Pointer (Select) */}
        <button 
          onClick={() => { setTool('select'); setConnectionStartId(null); }}
          className={`rounded-full p-2.5 transition-all text-zinc-700 ${tool === 'select' ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-100'}`}
          title="Selection pointer (V)"
        >
          <MousePointer size={15} />
        </button>

        {/* Pencil/Marker (Draw) */}
        <button 
          onClick={() => { setTool('draw'); setConnectionStartId(null); }}
          className={`rounded-full p-2.5 transition-all text-zinc-700 ${tool === 'draw' ? 'bg-zinc-900 text-purple-400' : 'hover:bg-zinc-100'}`}
          title="Draw tool (D)"
        >
          <svg className="h-[15px] w-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>

        {/* Sticky notes with color overlay */}
        <div className="relative group">
          <button 
            onClick={() => { setTool('sticky'); setConnectionStartId(null); }}
            style={{ color: tool === 'sticky' ? COLOR_MAP[stickyColor].text : '#374151' }}
            className={`flex items-center gap-1 rounded-full p-2 transition-all ${tool === 'sticky' ? 'bg-zinc-950 text-white' : 'hover:bg-zinc-100'}`}
            title="Sticky Note note (S)"
          >
            <FileText size={15} />
            <span className="h-2 w-2 rounded-full border border-black/10" style={{ backgroundColor: COLOR_MAP[stickyColor].bg }}></span>
          </button>
          
          {/* Hover Color Picker */}
          <div className="absolute bottom-11 left-1/2 -translate-x-1/2 hidden group-hover:flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white p-1.5 shadow-md z-30">
            {(Object.keys(COLOR_MAP) as StickyColor[]).map(color => (
              <button 
                key={color}
                onClick={(e) => { e.stopPropagation(); setStickyColor(color); setTool('sticky'); }}
                className={`h-4.5 w-4.5 rounded-full border transition-all ${stickyColor === color ? 'ring-2 ring-purple-600 scale-110' : 'border-zinc-300 hover:scale-105'}`}
                style={{ backgroundColor: COLOR_MAP[color].bg }}
              />
            ))}
          </div>
        </div>

        {/* Shape notes */}
        <div className="relative group">
          <button 
            onClick={() => { setTool('shape'); setConnectionStartId(null); }}
            className={`flex items-center gap-1.5 rounded-full p-2 transition-all text-zinc-700 ${tool === 'shape' ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-100'}`}
            title="Polygon geometric shape"
          >
            <Square size={15} />
            <span className="font-mono text-[9px] uppercase font-bold tracking-tight opacity-50">{shapeType}</span>
          </button>

          {/* Hover Shape Selector */}
          <div className="absolute bottom-11 left-1/2 -translate-x-1/2 hidden group-hover:flex items-center gap-2 rounded-lg border border-zinc-200 bg-white p-1.5 shadow-md z-30">
            {(['rectangle', 'circle', 'diamond'] as ShapeType[]).map(type => (
              <button 
                key={type}
                onClick={(e) => { e.stopPropagation(); setShapeType(type); setTool('shape'); }}
                className={`rounded border border-zinc-300 px-2 py-1 text-[9px] font-bold capitalize transition-all ${shapeType === type ? 'bg-zinc-900 text-white ring-2 ring-purple-500' : 'hover:bg-zinc-50'}`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Connectors / Arrows */}
        <button 
          onClick={() => { setTool('connection'); setConnectionStartId(null); }}
          className={`rounded-full p-2.5 transition-all text-zinc-700 ${tool === 'connection' ? 'bg-purple-600 text-white' : 'hover:bg-zinc-100'}`}
          title="Connection line (L)"
        >
          <ArrowUpRight size={15} />
        </button>

        {/* Stamps / Emotes */}
        <div className="relative group">
          <button 
            onClick={() => { setTool('stamp'); setConnectionStartId(null); }}
            className={`flex items-center gap-1 rounded-full p-2 transition-all text-zinc-700 ${tool === 'stamp' ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-100'}`}
            title="Stamp tool (E)"
          >
            <Smile size={15} />
            <span className="text-[11px] font-semibold">{STAMP_EMOJIS[stampType]}</span>
          </button>

          {/* Stamp selector hover menu */}
          <div className="absolute bottom-11 left-1/2 -translate-x-1/2 hidden group-hover:flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white p-1.5 shadow-md z-30">
            {(['heart', 'thumb', 'star', 'fire', 'smiley'] as StampType[]).map(stamp => (
              <button 
                key={stamp}
                onClick={(e) => { e.stopPropagation(); setStampType(stamp); setTool('stamp'); }}
                className={`text-base rounded p-1 transition-transform ${stampType === stamp ? 'bg-zinc-100 scale-110' : 'hover:scale-105'}`}
              >
                {STAMP_EMOJIS[stamp]}
              </button>
            ))}
          </div>
        </div>

        {/* Eraser */}
        <button 
          onClick={() => { setTool('eraser'); setConnectionStartId(null); }}
          className={`rounded-full p-2.5 transition-all text-zinc-700 ${tool === 'eraser' ? 'bg-red-500 text-white animate-pulse' : 'hover:bg-zinc-100'}`}
          title="Eraser (E)"
        >
          <Eraser size={15} />
        </button>

        {/* Clear Workspace */}
        <button 
          onClick={clearWhiteboard}
          className="rounded-full p-2.5 text-zinc-400 hover:text-red-500 transition-colors"
          title="Wipe canvas clean"
        >
          <Trash2 size={15} />
        </button>

      </div>

    </div>
  );
}

interface LandingPageProps {
  onGetStarted: () => void;
  onOpenCanvas: () => void;
  onOpenSticky: () => void;
}

export default function LandingPage({ onGetStarted, onOpenCanvas, onOpenSticky }: LandingPageProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(undefined);
  const [currentView, setCurrentView] = useState<'home' | 'privacy' | 'terms' | 'legal'>(() => {
    const path = window.location.pathname.toLowerCase();
    if (path.includes('privacy')) return 'privacy';
    if (path.includes('terms')) return 'terms';
    if (path.includes('legal')) return 'legal';
    return 'home';
  });

  useEffect(() => {
    // Dynamic SEO
    let title = 'Design Studio | The Visual Whiteboard for Your Tech Stack';
    let description = 'Design Studio is an open-source visual editing and content platform for building flowchart diagrams, structured notes, and project mockups developed by Caselgrid.';
    let path = '/';

    if (currentView === 'privacy') {
      title = 'Privacy Policy | Design Studio Beta';
      description = 'Privacy policy and data retention disclosures for Design Studio, a platform developed and maintained by Caselgrid.';
      path = '/privacypolicy';
    } else if (currentView === 'terms') {
      title = 'Terms & Conditions | Design Studio Beta';
      description = 'Terms of service and user agreement conditions for using the Design Studio whiteboard workspace at your own risk.';
      path = '/terms';
    } else if (currentView === 'legal') {
      title = 'Legal Notice | Design Studio Beta';
      description = 'Ownership attribution and legal disclaimers for Design Studio, an experimental beta product developed by Caselgrid.';
      path = '/legal';
    }

    document.title = title;
    
    // Update URL pathname if it changed
    if (window.location.pathname !== path) {
      window.history.pushState({ view: currentView }, title, path);
    }

    // Scroll container to top
    const scroller = document.querySelector('.overflow-y-auto');
    if (scroller) {
      scroller.scrollTop = 0;
    } else {
      window.scrollTo(0, 0);
    }
  }, [currentView]);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.toLowerCase();
      if (path.includes('privacy')) {
        setCurrentView('privacy');
      } else if (path.includes('terms')) {
        setCurrentView('terms');
      } else if (path.includes('legal')) {
        setCurrentView('legal');
      } else {
        setCurrentView('home');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplate(templateId);
    setTimeout(() => {
      setSelectedTemplate(undefined);
    }, 100);
  };

  const scrollToWhiteboard = () => {
    if (currentView !== 'home') {
      setCurrentView('home');
      setTimeout(() => {
        const arena = document.getElementById('whiteboard-arena');
        if (arena) arena.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const arena = document.getElementById('whiteboard-arena');
      if (arena) arena.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="absolute inset-0 overflow-y-auto bg-white text-zinc-900 selection:bg-purple-100 selection:text-purple-900">
      
      {/* Soft radial glowing gradients background layer */}
      <div className="absolute inset-x-0 top-0 h-[900px] pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-20%] left-[-15%] w-[60%] h-[70%] rounded-full bg-purple-200/25 blur-[130px]" />
        <div className="absolute top-[-10%] right-[-15%] w-[60%] h-[70%] rounded-full bg-orange-100/35 blur-[120px]" />
        <div className="absolute top-[25%] left-[20%] w-[55%] h-[60%] rounded-full bg-blue-100/30 blur-[145px]" />
        
        {/* Soft grid overlay fading out */}
        <div className="absolute inset-0 opacity-[0.05] dot-grid" style={{ maskImage: 'linear-gradient(to bottom, white 30%, transparent 95%)', WebkitMaskImage: 'linear-gradient(to bottom, white 30%, transparent 95%)' }}></div>
      </div>

      <div className="relative z-10 flex flex-col min-h-full justify-between">
        <div>
          {/* Header Bar */}
          <Header onOpenWhiteboard={scrollToWhiteboard} onGetStarted={onGetStarted} onGoHome={() => setCurrentView('home')} />

          {currentView === 'home' && (
            <>
              {/* Hero Section */}
              <main className="mx-auto max-w-7xl px-6 md:px-12 pt-16 md:pt-24 pb-12 space-y-20 relative">
                
                <section className="flex flex-col gap-12 lg:gap-16">
                  {/* Hero Copy (Text Only) */}
                  <div className="mx-auto max-w-4xl space-y-7 text-center flex flex-col items-center">
                    <h1 className="font-sans text-[48px] font-extrabold tracking-tight leading-[1.08] text-zinc-955 sm:text-[68px] text-center max-w-3xl">
                      The <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#9f1239] via-[#8b5cf6] to-[#ec4899]">visual</span> <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#ec4899] to-[#f59e0b]">whiteboard</span> for your tech stack
                    </h1>
                    
                    <p className="text-[17px] md:text-[18px] leading-relaxed text-zinc-555 font-medium text-center max-w-2xl">
                      Design Studio is an open-source visual editing and content platform for building flowchart diagrams, structured notes, and project mockups. Integrate with existing codebases. Ship incredibly fast.
                    </p>
                    
                    <div className="pt-2">
                      <button 
                        onClick={onGetStarted}
                        className="rounded-full bg-zinc-950 px-8 py-3.5 text-[15px] font-semibold text-white shadow-md hover:bg-black hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
                      >
                        Get started free
                      </button>
                    </div>
                  </div>

                  {/* HERO WHITEBOARD CANVAS SECTION */}
                  <div className="w-full pt-4">
                    <Whiteboard 
                      initialTemplate={selectedTemplate} 
                      onTemplateSelected={() => setSelectedTemplate(undefined)}
                    />
                  </div>

                </section>

                {/* INTERACTIVE CAROUSELS & FEATURE QUADRANTS */}
                <FeatureSections 
                  onSelectTemplate={handleSelectTemplate} 
                />

              </main>

              {/* VISUAL BUILDER WORKSPACE SHOWCASE */}
              <section className="bg-gradient-to-b from-[#f3f0f7] via-[#faf9fc] to-white border-none pt-24 pb-0 px-6 md:px-12 relative overflow-hidden">
                {/* Soft background glow */}
                <div className="absolute top-[10%] left-[-15%] w-[50%] h-[50%] rounded-full bg-purple-200/20 blur-[130px] pointer-events-none" />
                <div className="absolute bottom-[20%] right-[-10%] w-[45%] h-[50%] rounded-full bg-blue-100/20 blur-[130px] pointer-events-none" />
                
                <div className="max-w-7xl mx-auto space-y-12 relative z-10">
                  {/* Section Titles */}
                  <div className="text-center max-w-2xl mx-auto space-y-3">
                    <h2 className="font-sans text-3xl font-extrabold text-zinc-955 tracking-tight sm:text-4xl">
                      Build layouts exactly as you design
                    </h2>
                    <p className="text-[15.5px] text-zinc-550 max-w-xl mx-auto leading-relaxed font-semibold">
                      Design layout systems that compile cleanly into structural components. No manual measurements, no boilerplate coding.
                    </p>
                  </div>

                  {/* High Fidelity Visual Whiteboard Editor Mockup Grid */}
                  <div className="w-full flex justify-center">
                    
                    <div className="w-[98%] md:w-[92%] max-w-4xl bg-white rounded-t-3xl shadow-[0_-15px_40px_rgba(0,0,0,0.02)] min-h-[580px] relative select-none overflow-hidden pb-32 border-none">
                      {/* Grid Backdrop */}
                      <div className="absolute inset-0 opacity-[0.02] dot-grid pointer-events-none"></div>
                      
                      {/* Editor Page Header / Top Bar Mockup */}
                      <div className="flex items-center justify-between border-b border-zinc-100/80 px-6 py-3.5 text-xs font-semibold text-zinc-700 select-none">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-400 hover:text-zinc-700 cursor-pointer">📄</span>
                          <span className="text-zinc-800 font-extrabold text-xs">Product Launch Roadmap</span>
                          <span className="bg-purple-50 text-purple-600 text-[9px] font-extrabold px-1.5 py-0.5 rounded ml-1">Beta</span>
                        </div>
                        <div className="flex items-center gap-4">
                          {/* Avatar Stack */}
                          <div className="flex -space-x-1.5 items-center mr-1">
                            <div className="w-5.5 h-5.5 rounded-full border border-white bg-purple-400 text-[8px] font-bold text-white flex items-center justify-center">A</div>
                            <div className="w-5.5 h-5.5 rounded-full border border-white bg-orange-400 text-[8px] font-bold text-white flex items-center justify-center">D</div>
                            <div className="w-5.5 h-5.5 rounded-full border border-white bg-blue-500 text-[8px] font-bold text-white flex items-center justify-center flex-shrink-0">M</div>
                          </div>
                          <span className="text-zinc-350">|</span>
                          <button className="text-zinc-500 hover:text-zinc-950 font-bold transition-colors">Preview</button>
                          <button className="bg-zinc-950 hover:bg-black text-white px-3.5 py-1.5 rounded-full text-[11px] font-bold shadow-sm transition-all">Share</button>
                        </div>
                      </div>

                      {/* Main Editor Document Area */}
                      <div className="px-10 py-10 max-w-2xl mx-auto text-left space-y-6 relative">
                        
                        {/* Document Title */}
                        <div className="space-y-2 mb-4">
                          <h1 className="text-[32px] font-extrabold text-zinc-950 leading-tight">
                            Product Launch Roadmap
                          </h1>
                          <div className="flex items-center gap-2 text-zinc-400 text-[11px] font-semibold">
                            <span>Updated 2 hours ago</span>
                            <span>•</span>
                            <span>3 Collaborators</span>
                          </div>
                        </div>

                        {/* Paragraph block */}
                        <div className="relative pt-6">
                          {/* Inline Text Formatting Toolbar */}
                          <div className="absolute -top-3.5 left-[340px] flex items-center gap-1.5 bg-white border border-zinc-200 shadow-lg rounded-lg p-1 z-10 text-[11px] font-semibold text-zinc-700 select-none h-7">
                            <span className="hover:bg-zinc-105 px-1.5 py-0.5 rounded cursor-pointer inline-flex items-center gap-1 whitespace-nowrap">Sans <span className="text-[8px]">▼</span></span>
                            <div className="w-px h-3.5 bg-zinc-200"></div>
                            <span className="hover:bg-zinc-105 px-1.5 py-0.5 rounded cursor-pointer inline-flex items-center gap-1 whitespace-nowrap">14 <span className="text-[8px]">▼</span></span>
                            <div className="w-px h-3.5 bg-zinc-200"></div>
                            <button className="w-5 h-5 hover:bg-zinc-105 rounded flex items-center justify-center font-bold text-zinc-900">B</button>
                            <button className="w-5 h-5 hover:bg-zinc-105 rounded flex items-center justify-center italic text-zinc-700">I</button>
                            <button className="w-5 h-5 hover:bg-zinc-105 rounded flex items-center justify-center underline text-zinc-700">U</button>
                            <div className="w-px h-3.5 bg-zinc-200"></div>
                            {/* Text Color Picker */}
                            <button className="w-5 h-5 hover:bg-zinc-105 rounded flex flex-col items-center justify-center">
                              <span className="text-[10px] font-bold text-zinc-750 leading-none">A</span>
                              <div className="w-3.5 h-0.5 bg-purple-650"></div>
                            </button>
                            {/* Highlight Color Picker */}
                            <button className="w-5 h-5 hover:bg-zinc-105 rounded flex flex-col items-center justify-center">
                              <span className="text-[9px]">🖍️</span>
                              <div className="w-3.5 h-0.5 bg-yellow-400"></div>
                            </button>
                            <div className="w-px h-3.5 bg-zinc-200"></div>
                            <button className="w-5 h-5 hover:bg-zinc-105 rounded flex items-center justify-center">
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                              </svg>
                            </button>
                          </div>
                          
                          <p className="text-[14px] leading-relaxed text-zinc-650 font-medium">
                            Welcome to the workspace! Use this page to draft your <span className="bg-[#b3d8ff] text-zinc-950 px-1.5 py-0.5 rounded-sm font-semibold">layout specifications</span>, product requirements, and user stories. Press slash to invoke block components.
                          </p>
                        </div>

                        <p className="text-[14px] leading-relaxed text-zinc-650 font-medium">
                          Below, we can draft a Kanban board to outline our launch sprints, followed by H1/H2 headings to structure our user docs:
                        </p>

                        {/* Slash Command Trigger Block */}
                        <div className="relative pt-1">
                          <div className="flex items-center gap-1 text-[14px] font-medium text-zinc-950">
                            {/* Active typing cursor */}
                            <span className="w-0.5 h-5 bg-purple-600 animate-pulse inline-block"></span>
                            <span className="font-semibold text-zinc-950">/</span>
                          </div>

                          {/* Slash Command Dropdown Popover */}
                          <div className="absolute top-7 left-0 bg-white border-none rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.08)] p-1.5 w-64 z-20 hover:scale-[1.01] transition-transform">
                            
                            {/* Heading item */}
                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 cursor-pointer">
                              <div className="w-8 h-8 rounded bg-zinc-100 text-zinc-800 font-extrabold flex items-center justify-center text-xs">
                                H1
                              </div>
                              <div className="text-left">
                                <p className="text-xs font-bold text-zinc-800">Heading 1</p>
                                <p className="text-[10px] text-zinc-400 font-bold">Large section heading</p>
                              </div>
                            </div>

                            {/* Heading 2 */}
                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 cursor-pointer">
                              <div className="w-8 h-8 rounded bg-zinc-100 text-zinc-800 font-extrabold flex items-center justify-center text-xs">
                                H2
                              </div>
                              <div className="text-left">
                                <p className="text-xs font-bold text-zinc-800">Heading 2</p>
                                <p className="text-[10px] text-zinc-400 font-bold">Medium section heading</p>
                              </div>
                            </div>

                            {/* Table */}
                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 cursor-pointer">
                              <div className="w-8 h-8 rounded bg-[#19BCFE]/10 text-[#19BCFE] flex items-center justify-center">
                                📊
                              </div>
                              <div className="text-left">
                                <p className="text-xs font-bold text-zinc-800">Table</p>
                                <p className="text-[10px] text-zinc-400 font-bold">Organize info in rows and columns</p>
                              </div>
                            </div>

                            {/* Columns */}
                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 cursor-pointer">
                              <div className="w-8 h-8 rounded bg-zinc-100 text-zinc-550 flex items-center justify-center text-xs">
                                ☵
                              </div>
                              <div className="text-left">
                                <p className="text-xs font-bold text-zinc-800">Columns</p>
                                <p className="text-[10px] text-zinc-400 font-bold">Add multi-column blocks</p>
                              </div>
                            </div>

                            {/* Kanban Board - Highlighted/Selected Item */}
                            <div className="flex items-center gap-3 p-2 rounded-lg bg-purple-50 border border-purple-100 cursor-pointer">
                              <div className="w-8 h-8 rounded bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-extrabold">
                                📋
                              </div>
                              <div className="text-left">
                                <p className="text-xs font-extrabold text-purple-900 flex items-center gap-1.5">
                                  Kanban Board
                                  <span className="text-[8px] bg-purple-200 text-purple-800 px-1 py-0.2 rounded uppercase font-black">Hot</span>
                                </p>
                                <p className="text-[10px] text-purple-500 font-semibold">Visualize project card states</p>
                              </div>
                            </div>

                          </div>

                          {/* Floating Cursor pointer pointing to highlighted item */}
                          <div className="absolute top-[200px] left-[180px] z-30 flex items-center gap-0.5 pointer-events-none">
                            <svg className="h-4 w-4 drop-shadow-sm rotate-[135deg]" viewBox="0 0 24 24" fill="none">
                              <path d="M4.5 3V17L9 12.5L14.5 18L17.5 15L12 9.5L17.5 9.5L4.5 3Z" fill="#FAF9FC" stroke="#8b5cf6" strokeWidth="2.5" />
                            </svg>
                            <span className="bg-[#8b5cf6] text-white text-[8.5px] font-extrabold px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap">
                              Developer
                            </span>
                          </div>

                        </div>

                        {/* Preview elements fading into bottom background */}
                        <div className="pt-24 space-y-6 opacity-35">
                          {/* Kanban Mockup Preview (faded) */}
                          <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50/50 space-y-3">
                            <div className="flex items-center justify-between text-[11px] font-bold text-zinc-400">
                              <span>Sprint Board</span>
                              <span>3 Cards</span>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div className="bg-white border border-zinc-150 rounded-lg p-2.5 space-y-2">
                                <div className="h-2 w-10 bg-green-100 rounded" />
                                <div className="h-1.5 w-full bg-zinc-200 rounded" />
                                <div className="h-1.5 w-[70%] bg-zinc-200 rounded" />
                              </div>
                              <div className="bg-white border border-zinc-150 rounded-lg p-2.5 space-y-2">
                                <div className="h-2 w-10 bg-purple-100 rounded" />
                                <div className="h-1.5 w-full bg-zinc-200 rounded" />
                                <div className="h-1.5 w-[85%] bg-zinc-200 rounded" />
                              </div>
                              <div className="bg-white border border-zinc-150 rounded-lg p-2.5 space-y-2">
                                <div className="h-2 w-10 bg-blue-100 rounded" />
                                <div className="h-1.5 w-full bg-zinc-200 rounded" />
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>
                      
                      {/* Gradient Mask to Merge Mockup with the Page Background */}
                      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-white via-white/90 to-transparent z-20 pointer-events-none" />
                    </div>

                  </div>
                </div>
              </section>{/* PILLS INTERACTIVE CTA GRID (BOTTOM MOST ABOVE FOOTER) */}
              <section className="text-center py-20 px-6 bg-white space-y-10 border-b border-zinc-100">
                <div className="max-w-xl mx-auto space-y-3">
                  <h2 className="font-sans text-3xl font-extrabold text-zinc-955 tracking-tight">
                    Explore what you can do with Design Studio
                  </h2>
                  <p className="text-[14.5px] text-zinc-500 leading-normal font-semibold">
                    Whether you are designing a mockup, mapping coding logic, or framing templates, get connected in one click.
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-4">
                  <button 
                    onClick={onOpenCanvas}
                    className="rounded-full bg-[#19BCFE] hover:bg-[#15a4fa] text-black font-extrabold text-[22px] sm:text-[30px] px-9 py-4 hover:scale-[1.04] active:scale-95 transition-all shadow-md tracking-tight whitespace-nowrap"
                  >
                    Art Studio
                  </button>
                  <button 
                    onClick={onGetStarted}
                    className="rounded-full bg-[#0ACF83] hover:bg-[#09be78] text-black font-extrabold text-[22px] sm:text-[30px] px-9 py-4 hover:scale-[1.04] active:scale-95 transition-all shadow-md tracking-tight whitespace-nowrap"
                  >
                    Docs
                  </button>
                  <button 
                    onClick={onOpenSticky}
                    className="rounded-full bg-[#FFBC93] hover:bg-[#ffa774] text-black font-extrabold text-[22px] sm:text-[30px] px-9 py-4 hover:scale-[1.04] active:scale-95 transition-all shadow-md tracking-tight whitespace-nowrap"
                  >
                    Screenrecorder
                  </button>
                  <button 
                    onClick={onGetStarted}
                    className="rounded-full bg-zinc-950 hover:bg-black text-white font-extrabold text-[22px] sm:text-[30px] px-9 py-4 hover:scale-[1.04] active:scale-95 transition-all shadow-md tracking-tight whitespace-nowrap"
                  >
                    Get started
                  </button>
                </div>
              </section>
            </>
          )}

          {currentView === 'privacy' && <PrivacyPolicy onBack={() => setCurrentView('home')} />}
          {currentView === 'terms' && <TermsAndConditions onBack={() => setCurrentView('home')} />}
          {currentView === 'legal' && <LegalNotice onBack={() => setCurrentView('home')} />}
        </div>

        {/* Footer Area */}
        <Footer onNavigate={setCurrentView} />
      </div>

    </div>
  );
}